import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Assessment as ReportsIcon,
  CalendarToday as CalIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  DragHandle as DragHandleIcon,
  FactCheck as FactCheckIcon,
  FilterList as FilterListIcon,
  InfoOutlined as InfoIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Replay as ReplayIcon,
  KeyboardArrowRight as ArrowRightIcon,
  LocationOn as LocationOnIcon,
  Map as MapIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  TableView as XlsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { tenantApi } from '@/api/tenant';
import { ordersApi } from '@/api/orders';
import { searchCrossTenantProducts } from '@/api/products';
import type { CrossTenantProduct } from '@/api/products';
import { formatNearExpiry } from '@/utils/nearExpiry';
import { getMyRepRequests } from '@/api/repRequests';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from '@/stores/cartStore';
import { useAppSettings } from '@/hooks/useAppSettings';
import { orderTemplatesApi } from '@/api/orderTemplatesApi';
import { orderPreferencesApi } from '@/api/orderPreferences';
import { PaginationFooter } from '@/components/PaginationFooter';
import { PartyDetailDialog } from '@/components/PartyDetailDialog';
import { OutstandingInvoicesDialog } from '@/components/OutstandingInvoicesDialog';
import type { PeerTenant } from '@/types';
import type { Order, OrderStatus } from '@/types/order';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderItem {
  key: string;
  product: CrossTenantProduct;
  qty: number;
  freeQty?: number;
}

interface DraftOrder {
  id: string;
  name: string;
  items: OrderItem[];
  savedAt: string;
  itemCount?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function capQtyToAvailableWithScheme(raw: number, scheme: string | undefined, availableQty: number | undefined | null, stockHidden: boolean | undefined): number {
  if (!availableQty || availableQty <= 0 || stockHidden) return raw;
  const { paid, free } = computeSchemeBreakdown(raw, scheme);
  if (paid + free <= availableQty) return raw;
  for (let q = raw - 1; q >= 1; q--) {
    const { paid: p, free: f } = computeSchemeBreakdown(q, scheme);
    if (p + f <= availableQty) return q;
  }
  return 1;
}

/**
 * Computes free qty for a "buy X get Y free" scheme, honoring three
 * priorities in strict order:
 *   1. paid + free must ALWAYS be a whole integer.
 *   2. paid and free must always be multiples of 0.5.
 *   3. never bump the entered qty by 1 or more — only ever by less than 1.
 *
 * Entered qty is always a whole integer, so the only two 0.5-grid points
 * within [qty, qty+1) are `qty` itself and `qty + 0.5`:
 *   Candidate A (no bump): qty stays whole -> free must be whole (floor).
 *   Candidate B (+0.5 bump): qty ends in .5 -> free must also end in .5
 *                            (that's the only way qty+free stays whole).
 * Whichever candidate gives MORE free wins (most faithful to the scheme
 * ratio); a tie goes to Candidate A (no bump), per priority 3.
 */
function computeSchemeBreakdown(enteredQty: number, scheme: string | undefined): { paid: number; free: number } {
  const fallback = { paid: enteredQty, free: 0 };
  if (!scheme || enteredQty <= 0) return fallback;
  const parts = scheme.split(':');
  if (parts.length !== 2) return fallback;
  const buy = parseInt(parts[0], 10);
  const freeN = parseInt(parts[1], 10);
  if (isNaN(buy) || isNaN(freeN) || buy <= 0 || freeN <= 0) return fallback;

  const qty = Math.floor(enteredQty);
  const rate = freeN / buy;

  const freeA = Math.floor(rate * qty + 1e-9);                          // Candidate A — no bump
  const qtyB = qty + 0.5;                                               // Candidate B — +0.5 bump
  const freeExactB = rate * qtyB;
  const freeB = freeExactB >= 0.5 - 1e-9 ? Math.floor(freeExactB - 0.5 + 1e-9) + 0.5 : null;

  if (freeB !== null && freeB > freeA + 1e-9) return { paid: qtyB, free: freeB };
  return { paid: qty, free: freeA };
}

function stockColor(qty: number | undefined, threshold: number): string {
  if (qty === undefined || qty === null) return '#999';
  if (qty <= 0) return '#b91c1c';
  if (qty <= threshold) return '#ea580c';
  return '#15803d';
}

function stockRowBg(qty: number | undefined, threshold: number): string {
  if (qty === undefined || qty === null) return 'transparent';
  if (qty <= 0) return '#FAC8C8';
  if (qty <= threshold) return '#FBCEB1';
  return '#E8FCE8';
}

// DRAFTS_KEY kept for one-time migration of legacy localStorage data
const DRAFTS_KEY = 'my_orders_drafts';

// ─── Filter persistence (session-only — survives nav away/back, clears on browser close) ──
const FILTERS_SESSION_PREFIX = 'orderFilters:';
function loadSessionFilters<T>(key: string): Partial<T> {
  try {
    const raw = sessionStorage.getItem(FILTERS_SESSION_PREFIX + key);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveSessionFilters<T extends object>(key: string, data: T) {
  try { sessionStorage.setItem(FILTERS_SESSION_PREFIX + key, JSON.stringify(data)); } catch {}
}

// ─── Shortage detection ────────────────────────────────────────────────────
interface ShortageEntry {
  sku: string;
  productName: string;
  orderedQty: number;
  receivedQty: number;
  shortageQty: number;
}
interface OrderShortageRecord {
  orderId: number;
  shortages: ShortageEntry[];
}

/** Groups the backend's flat shortage list by order id for the per-row badges/details. */
function groupShortagesByOrder(rows: { orderId: number; sku: string; productName: string; orderedQty: number; receivedQty: number; shortageQty: number }[]): Map<number, OrderShortageRecord> {
  const map = new Map<number, OrderShortageRecord>();
  for (const row of rows) {
    const rec = map.get(row.orderId) ?? { orderId: row.orderId, shortages: [] };
    rec.shortages.push({ sku: row.sku, productName: row.productName, orderedQty: row.orderedQty, receivedQty: row.receivedQty, shortageQty: row.shortageQty });
    map.set(row.orderId, rec);
  }
  return map;
}

/**
 * Intelligent invoice CSV parser.
 *
 * Column detection strategy (in priority order):
 *   1. Exact header name match (case-insensitive)
 *   2. Header contains the candidate keyword
 *   3. Numeric correlation: score each column by how many of its values
 *      match expected order quantities from the order items — the best-
 *      scoring column wins.  This handles invoices with non-standard or
 *      vernacular column names (e.g. "Nos", "Pcs", "Pieces", "Matra", …).
 *
 * Product matching:
 *   SKU exact → Product name exact → Product name partial (one contains other)
 *
 * Free items: detected separately from the "Free/Bonus/Scheme" column and
 * compared against the order's freeQty so scheme shortages are also caught.
 */
function parseInvoiceCsv(
  csvText: string,
  orderItems: import('@/types/order').OrderItem[],
): ShortageEntry[] {
  // ── Normalise & split ────────────────────────────────────────────────────
  const lines = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  // Quote-aware CSV cell splitter
  const splitRow = (row: string): string[] => {
    const cells: string[] = [];
    let cur = '';
    let inQ = false;
    for (const ch of row) {
      if (ch === '"') { inQ = !inQ; }
      else if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    cells.push(cur.trim());
    return cells.map(c => c.replace(/^"|"$/g, '').trim());
  };

  const hdrs     = splitRow(lines[0]).map(h => h.toLowerCase());
  const dataRows = lines.slice(1).map(splitRow);
  const n        = hdrs.length;

  // ── Header-keyword column finder ─────────────────────────────────────────
  const findByHeader = (...cands: string[]): number => {
    for (const c of cands) { const i = hdrs.indexOf(c);                  if (i >= 0) return i; }
    for (const c of cands) { const i = hdrs.findIndex(h => h.includes(c)); if (i >= 0) return i; }
    return -1;
  };

  const skuIdx  = findByHeader('sku', 'code', 'item code', 'item_code', 'product code',
                                'barcode', 'item no', 'itemno', 'part no', 'hsn');
  const nameIdx = findByHeader('product name', 'product', 'item name', 'item', 'name',
                                'description', 'particulars', 'medicine', 'drug', 'brand',
                                'article', 'goods');
  let qtyIdx    = findByHeader('qty', 'quantity', 'packs', 'pack qty', 'units', 'nos',
                                'boxes', 'pieces', 'pcs', 'billed qty', 'invoice qty',
                                'bill qty', 'sold qty', 'received', 'dispatched qty',
                                'delivered qty', 'ordered qty');
  let freeIdx   = findByHeader('free', 'fr', 'free qty', 'freeqty', 'free_qty', 'bonus',
                                'scheme qty', 'scm qty', 'scm', 'gratis', 'comp',
                                'complimentary', 'gift', 'extra', 'addl', 'scheme');

  // ── Build expected-value maps from order items ────────────────────────────
  const expQtyBySku   = new Map<string, number>();
  const expQtyByName  = new Map<string, number>();
  const expFreeBySku  = new Map<string, number>();
  const expFreeByName = new Map<string, number>();

  for (const item of orderItems) {
    const sk = (item.sku          ?? '').toLowerCase();
    const nm = (item.productName  ?? '').toLowerCase();
    const q  = Number(item.qty);
    const f  = Number(item.freeQty ?? 0);
    if (sk) { expQtyBySku.set(sk, q);  expFreeBySku.set(sk, f); }
    if (nm) { expQtyByName.set(nm, q); expFreeByName.set(nm, f); }
  }

  // Helper: partial name lookup (one string contains the other)
  const partialLookup = <V,>(map: Map<string, V>, key: string): V | undefined => {
    for (const [k, v] of map) {
      if (key.length >= 3 && k.length >= 3 && (key.includes(k) || k.includes(key))) return v;
    }
    return undefined;
  };

  // ── Numeric correlation to detect qty / free columns ─────────────────────
  if (qtyIdx === -1 || freeIdx === -1) {
    const qtyScore  = new Array<number>(n).fill(0);
    const freeScore = new Array<number>(n).fill(0);

    for (const row of dataRows) {
      const rowSku  = skuIdx  >= 0 ? (row[skuIdx]  ?? '').toLowerCase() : '';
      const rowName = nameIdx >= 0 ? (row[nameIdx] ?? '').toLowerCase() : '';

      const eQ = expQtyBySku.get(rowSku)   ?? expQtyByName.get(rowName)
               ?? partialLookup(expQtyByName, rowName) ?? null;
      const eF = expFreeBySku.get(rowSku)  ?? expFreeByName.get(rowName)
               ?? partialLookup(expFreeByName, rowName) ?? null;

      for (let c = 0; c < n; c++) {
        if (c === skuIdx || c === nameIdx) continue;
        const v = parseFloat(row[c] ?? '');
        if (isNaN(v)) continue;
        if (eQ !== null && Math.abs(v - eQ) < 0.01)              qtyScore[c]++;
        if (eF !== null && eF > 0 && Math.abs(v - eF) < 0.01)   freeScore[c]++;
      }
    }

    if (qtyIdx === -1) {
      const best = Math.max(...qtyScore);
      if (best > 0) qtyIdx = qtyScore.indexOf(best);
    }
    if (freeIdx === -1) {
      const best = Math.max(...freeScore);
      if (best > 0) {
        const cand = freeScore.indexOf(best);
        freeIdx = cand !== qtyIdx ? cand : (() => {
          // Second-best column
          const scores = [...freeScore];
          scores[cand] = -1;
          const b2 = Math.max(...scores);
          return b2 > 0 ? scores.indexOf(b2) : -1;
        })();
      }
    }
  }

  if (qtyIdx === -1) return []; // Cannot determine quantity column

  // ── Accumulate received amounts ───────────────────────────────────────────
  type Rec = { qty: number; free: number };
  const recBySku  = new Map<string, Rec>();
  const recByName = new Map<string, Rec>();

  const add = (map: Map<string, Rec>, key: string, qty: number, free: number) => {
    if (!key) return;
    const p = map.get(key) ?? { qty: 0, free: 0 };
    map.set(key, { qty: p.qty + qty, free: p.free + free });
  };

  for (const row of dataRows) {
    const qty  = Math.max(0, parseFloat(row[qtyIdx] ?? '') || 0);
    const free = freeIdx >= 0 ? Math.max(0, parseFloat(row[freeIdx] ?? '') || 0) : 0;
    const sk   = skuIdx  >= 0 ? (row[skuIdx]  ?? '').toLowerCase() : '';
    const nm   = nameIdx >= 0 ? (row[nameIdx] ?? '').toLowerCase() : '';
    add(recBySku,  sk, qty, free);
    add(recByName, nm, qty, free);
  }

  // ── Compare against order items and build shortage list ───────────────────
  return orderItems.reduce<ShortageEntry[]>((acc, item) => {
    const sk = (item.sku         ?? '').toLowerCase();
    const nm = (item.productName ?? '').toLowerCase();

    // SKU exact → name exact → name partial
    const rec: Rec =
      recBySku.get(sk) ??
      recByName.get(nm) ??
      partialLookup(recByName, nm) ??
      { qty: 0, free: 0 };

    const ordQty  = Number(item.qty);
    const ordFree = Number(item.freeQty ?? 0);
    const qtyShort  = Math.max(0, ordQty  - rec.qty);
    const freeShort = freeIdx >= 0 ? Math.max(0, ordFree - rec.free) : 0;
    const total = qtyShort + freeShort;

    if (total > 0) {
      acc.push({
        sku:         item.sku,
        productName: item.productName,
        orderedQty:  ordQty + ordFree,
        receivedQty: rec.qty + rec.free,
        shortageQty: total,
      });
    }
    return acc;
  }, []);
}

function loadLegacyDrafts(tenantId: number): DraftOrder[] {
  try {
    const raw = localStorage.getItem(`${DRAFTS_KEY}_${tenantId}`);
    if (raw) return JSON.parse(raw) as DraftOrder[];
  } catch { /* ignore */ }
  return [];
}

const PLACE_ORDER_ITEMS_KEY = 'my_orders_place_items';

function loadPlaceOrderItems(tenantId: number): OrderItem[] {
  try {
    const raw = localStorage.getItem(`${PLACE_ORDER_ITEMS_KEY}_${tenantId}`);
    if (raw) return JSON.parse(raw) as OrderItem[];
  } catch { /* ignore */ }
  return [];
}

function savePlaceOrderItems(tenantId: number, items: OrderItem[]) {
  localStorage.setItem(`${PLACE_ORDER_ITEMS_KEY}_${tenantId}`, JSON.stringify(items));
}

const TPL_COUNTS_KEY = 'tpl_item_counts';
function loadTplCounts(tenantId: number): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(`${TPL_COUNTS_KEY}_${tenantId}`) ?? '{}'); } catch { return {}; }
}
function setTplCount(tenantId: number, id: string, count: number) {
  const c = loadTplCounts(tenantId); c[id] = count;
  localStorage.setItem(`${TPL_COUNTS_KEY}_${tenantId}`, JSON.stringify(c));
}
function delTplCount(tenantId: number, id: string) {
  const c = loadTplCounts(tenantId); delete c[id];
  localStorage.setItem(`${TPL_COUNTS_KEY}_${tenantId}`, JSON.stringify(c));
}

// ---------------------------------------------------------------------------
// Table styles
// ---------------------------------------------------------------------------

const tblHead: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 11,
  fontWeight: 700,
  color: '#666',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tblCell: React.CSSProperties = {
  padding: '4px 8px',
  fontSize: 12,
  borderBottom: '1px solid #eeeeee',
  verticalAlign: 'middle',
  overflow: 'hidden',
};

// ---------------------------------------------------------------------------
// DistributorSidebar
// ---------------------------------------------------------------------------

const PAGE_SIZE = 100;

interface DistributorSidebarProps {
  allDistributors: PeerTenant[];
  priorityIds: number[];
  onReorder: (ids: number[]) => void;
  onRemove: (id: number) => void;
  onAdd: (id: number) => void;
}

function DistributorSidebar({
  allDistributors,
  priorityIds,
  onReorder,
  onRemove,
  onAdd,
}: DistributorSidebarProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [dlgSearch, setDlgSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const priorityList = priorityIds
    .map(id => allDistributors.find(p => p.id === id))
    .filter((p): p is PeerTenant => p !== undefined);

  function openPanel() {
    setDlgSearch('');
    setVisibleCount(PAGE_SIZE);
    setPanelOpen(true);
  }

  const filteredAll = allDistributors.filter(p => {
    if (!dlgSearch.trim()) return true;
    const q = dlgSearch.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      (p.city ?? '').toLowerCase().includes(q) ||
      (p.district ?? '').toLowerCase().includes(q)
    );
  });

  const visibleList = filteredAll.slice(0, visibleCount);
  const hasMore = filteredAll.length > visibleCount;

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleDrop(dropIndex: number) {
    const from = dragIndexRef.current;
    if (from === null || from === dropIndex) return;
    const next = [...priorityIds];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    onReorder(next);
    dragIndexRef.current = null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.8 }}>
          SUPPLIER PRIORITY
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {priorityList.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, fontSize: 12 }}>
            No suppliers added. Click the button below.
          </Typography>
        )}
        {priorityList.map((peer, index) => (
          <Box
            key={peer.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(index)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 0.75,
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: 'grab',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
              userSelect: 'none',
            }}
          >
            <DragHandleIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5, flexShrink: 0 }} />
            <Box
              sx={{
                minWidth: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: '#152346',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                mr: 0.75,
                flexShrink: 0,
              }}
            >
              {index + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
                {peer.name}
              </Typography>
              {peer.city && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }} noWrap>
                  {peer.city}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setConfirmRemoveId(peer.id)} sx={{ flexShrink: 0, ml: 0.25, p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          onClick={openPanel}
          sx={{ fontSize: 11, textTransform: 'none' }}
        >
          Add / Remove Suppliers
        </Button>
      </Box>

      {/* ---- Full distributor panel dialog ---- */}
      <Dialog
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ py: 1.5, pr: 6, fontSize: 15, fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>
          Add / Remove Suppliers
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: 11 }}>
            ({allDistributors.length} available)
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPanelOpen(false)}
            sx={{ position: 'absolute', right: 10, top: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, city or district…"
            value={dlgSearch}
            onChange={e => {
              setDlgSearch(e.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
          />
        </Box>

        <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
          {filteredAll.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No suppliers found.
            </Typography>
          ) : (
            <>
              {visibleList.map((peer, idx) => {
                const isAdded = priorityIds.includes(peer.id);
                return (
                  <Box
                    key={peer.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                      borderBottom: '1px solid #f5f5f5',
                      bgcolor: isAdded ? '#f0f7ff' : 'background.paper',
                      '&:hover': { bgcolor: isAdded ? '#e3f0ff' : '#fafafa' },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ width: 28, flexShrink: 0, color: 'text.disabled', fontSize: 12, fontWeight: 600 }}
                    >
                      {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#1a56db',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {peer.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {[peer.city, peer.district].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={isAdded ? 'outlined' : 'contained'}
                      color={isAdded ? 'error' : 'primary'}
                      onClick={() => (isAdded ? onRemove(peer.id) : onAdd(peer.id))}
                      sx={{
                        fontSize: 11,
                        minWidth: 72,
                        flexShrink: 0,
                        fontWeight: 700,
                        ...(isAdded
                          ? {}
                          : { bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }),
                      }}
                    >
                      {isAdded ? 'REMOVE' : 'ADD'}
                    </Button>
                  </Box>
                );
              })}

              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                    sx={{ fontSize: 12 }}
                  >
                    Load more ({filteredAll.length - visibleCount} remaining)
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm remove dialog */}
      <Dialog open={confirmRemoveId !== null} onClose={() => setConfirmRemoveId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Remove Supplier</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Remove <strong>{allDistributors.find(d => d.id === confirmRemoveId)?.name}</strong> from your priority list?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmRemoveId(null)} size="small">Cancel</Button>
          <Button
            onClick={() => { if (confirmRemoveId !== null) onRemove(confirmRemoveId); setConfirmRemoveId(null); }}
            variant="contained" size="small" color="error"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CitySidebar
// ---------------------------------------------------------------------------

interface CitySidebarProps {
  allCities: string[];
  preferredCities: string[];
  defaultCity?: string;
  onReorder: (cities: string[]) => void;
  onRemove: (city: string) => void;
  onAdd: (city: string) => void;
}

function CitySidebar({ allCities, preferredCities, defaultCity, onReorder, onRemove, onAdd }: CitySidebarProps) {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgSearch, setDlgSearch] = useState('');
  const [confirmCity, setConfirmCity] = useState<string | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const filteredAll = allCities.filter(c => !dlgSearch.trim() || c.toLowerCase().includes(dlgSearch.toLowerCase()));

  function handleDragStart(i: number) { dragIndexRef.current = i; }
  function handleDragOver(e: React.DragEvent) { e.preventDefault(); }
  function handleDrop(dropIdx: number) {
    const from = dragIndexRef.current;
    if (from === null || from === dropIdx) return;
    const next = [...preferredCities];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    onReorder(next);
    dragIndexRef.current = null;
  }

  function requestRemove(city: string) {
    if (city === defaultCity) return;
    setConfirmCity(city);
  }

  function confirmRemove() {
    if (confirmCity) onRemove(confirmCity);
    setConfirmCity(null);
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.8 }}>
          PREFERRED CITIES
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {preferredCities.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, fontSize: 12 }}>
            No cities added yet.
          </Typography>
        )}
        {preferredCities.map((city, index) => {
          const isDefault = city === defaultCity;
          return (
            <Box
              key={city}
              draggable={!isDefault}
              onDragStart={() => !isDefault && handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              sx={{
                display: 'flex', alignItems: 'center', px: 0.75, py: 0.75,
                borderBottom: '1px solid', borderColor: 'divider',
                cursor: isDefault ? 'default' : 'grab',
                bgcolor: isDefault ? '#f0f7ff' : 'background.paper',
                '&:hover': { bgcolor: isDefault ? '#f0f7ff' : 'action.hover' },
                userSelect: 'none',
              }}
            >
              <DragHandleIcon sx={{ fontSize: 18, color: isDefault ? 'transparent' : 'text.disabled', mr: 0.5, flexShrink: 0 }} />
              <Box sx={{ minWidth: 20, height: 20, borderRadius: '4px', bgcolor: '#152346', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, mr: 0.75, flexShrink: 0 }}>
                {index + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
                  {city}
                </Typography>
                {isDefault && (
                  <Typography variant="caption" sx={{ fontSize: 9, color: '#1a56db', fontWeight: 700, letterSpacing: 0.3 }}>
                    HOME
                  </Typography>
                )}
              </Box>
              <Tooltip title={isDefault ? 'Home city cannot be removed' : 'Remove'} placement="left">
                <span>
                  <IconButton
                    size="small"
                    disabled={isDefault}
                    onClick={() => requestRemove(city)}
                    sx={{ flexShrink: 0, ml: 0.25, p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth size="small" startIcon={<AddIcon />} onClick={() => { setDlgSearch(''); setDlgOpen(true); }} sx={{ fontSize: 11, textTransform: 'none' }}>
          Add / Remove Cities
        </Button>
      </Box>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { height: '70vh', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle sx={{ py: 1.5, pr: 6, fontSize: 15, fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>
          Add / Remove Cities
          <IconButton size="small" onClick={() => setDlgOpen(false)} sx={{ position: 'absolute', right: 10, top: 10 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <TextField fullWidth size="small" placeholder="Search cities…" value={dlgSearch} onChange={e => setDlgSearch(e.target.value)} autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
          />
        </Box>
        <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
          {filteredAll.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>No cities found.</Typography>
          ) : filteredAll.map((city, idx) => {
            const isAdded = preferredCities.includes(city);
            const isDefault = city === defaultCity;
            return (
              <Box key={city} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #f5f5f5', bgcolor: isAdded ? '#f0f7ff' : 'background.paper', '&:hover': { bgcolor: isAdded ? '#e3f0ff' : '#fafafa' } }}>
                <Typography variant="caption" sx={{ width: 28, flexShrink: 0, color: 'text.disabled', fontSize: 12, fontWeight: 600 }}>{idx + 1}</Typography>
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a56db' }} noWrap>{city}</Typography>
                  {isDefault && <Typography sx={{ fontSize: 9, color: '#1a56db', fontWeight: 700 }}>HOME</Typography>}
                </Box>
                <Tooltip title={isDefault ? 'Home city cannot be removed' : ''} placement="left">
                  <span>
                    <Button
                      size="small"
                      variant={isAdded ? 'outlined' : 'contained'}
                      color={isAdded ? 'error' : 'primary'}
                      disabled={isDefault && isAdded}
                      onClick={() => {
                        if (isAdded) {
                          setDlgOpen(false);
                          setConfirmCity(city);
                        } else {
                          onAdd(city);
                        }
                      }}
                      sx={{ fontSize: 11, minWidth: 72, flexShrink: 0, fontWeight: 700, ...(!isAdded && { bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }) }}
                    >
                      {isAdded ? 'REMOVE' : 'ADD'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>

      {/* Confirm remove dialog */}
      <Dialog open={Boolean(confirmCity)} onClose={() => setConfirmCity(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Remove City</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Remove <strong>{confirmCity}</strong> from your city priority list?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmCity(null)} size="small">Cancel</Button>
          <Button onClick={confirmRemove} variant="contained" size="small" color="error">Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LocationSidebar
// ---------------------------------------------------------------------------

interface LocationSidebarProps {
  allLocations: string[];
  priorityLocations: string[];
  onReorder: (locs: string[]) => void;
  onRemove: (loc: string) => void;
  onAdd: (loc: string) => void;
}

function LocationSidebar({ allLocations, priorityLocations, onReorder, onRemove, onAdd }: LocationSidebarProps) {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgSearch, setDlgSearch] = useState('');
  const [confirmLoc, setConfirmLoc] = useState<string | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const filteredAll = allLocations.filter(l => !dlgSearch.trim() || l.toLowerCase().includes(dlgSearch.toLowerCase()));

  function handleDragStart(i: number) { dragIndexRef.current = i; }
  function handleDragOver(e: React.DragEvent) { e.preventDefault(); }
  function handleDrop(dropIdx: number) {
    const from = dragIndexRef.current;
    if (from === null || from === dropIdx) return;
    const next = [...priorityLocations];
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    onReorder(next);
    dragIndexRef.current = null;
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.8 }}>
          AREA PRIORITY
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {priorityLocations.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, fontSize: 12 }}>
            No areas added yet. Click the button below.
          </Typography>
        )}
        {priorityLocations.map((loc, index) => (
          <Box
            key={loc}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(index)}
            sx={{
              display: 'flex', alignItems: 'center', px: 0.75, py: 0.75,
              borderBottom: '1px solid', borderColor: 'divider',
              cursor: 'grab', bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none',
            }}
          >
            <DragHandleIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5, flexShrink: 0 }} />
            <Box sx={{ minWidth: 20, height: 20, borderRadius: '4px', bgcolor: '#152346', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, mr: 0.75, flexShrink: 0 }}>
              {index + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }} noWrap>{loc}</Typography>
            </Box>
            <Tooltip title="Remove" placement="left">
              <IconButton size="small" onClick={() => setConfirmLoc(loc)} sx={{ flexShrink: 0, ml: 0.25, p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button fullWidth size="small" startIcon={<AddIcon />} onClick={() => { setDlgSearch(''); setDlgOpen(true); }} sx={{ fontSize: 11, textTransform: 'none' }}>
          Add / Remove Areas
        </Button>
      </Box>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { height: '70vh', display: 'flex', flexDirection: 'column' } }}>
        <DialogTitle sx={{ py: 1.5, pr: 6, fontSize: 15, fontWeight: 700, borderBottom: '1px solid #e0e0e0' }}>
          Add / Remove Areas
          <IconButton size="small" onClick={() => setDlgOpen(false)} sx={{ position: 'absolute', right: 10, top: 10 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <TextField fullWidth size="small" placeholder="Search areas…" value={dlgSearch} onChange={e => setDlgSearch(e.target.value)} autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
          />
        </Box>
        <DialogContent sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
          {allLocations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              No areas available. Add preferred cities first.
            </Typography>
          ) : filteredAll.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>No areas found.</Typography>
          ) : filteredAll.map((loc, idx) => {
            const isAdded = priorityLocations.includes(loc);
            return (
              <Box key={loc} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #f5f5f5', bgcolor: isAdded ? '#f0f7ff' : 'background.paper', '&:hover': { bgcolor: isAdded ? '#e3f0ff' : '#fafafa' } }}>
                <Typography variant="caption" sx={{ width: 28, flexShrink: 0, color: 'text.disabled', fontSize: 12, fontWeight: 600 }}>{idx + 1}</Typography>
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a56db' }} noWrap>{loc}</Typography>
                </Box>
                <Button
                  size="small"
                  variant={isAdded ? 'outlined' : 'contained'}
                  color={isAdded ? 'error' : 'primary'}
                  onClick={() => {
                    if (isAdded) { setDlgOpen(false); setConfirmLoc(loc); }
                    else onAdd(loc);
                  }}
                  sx={{ fontSize: 11, minWidth: 72, flexShrink: 0, fontWeight: 700, ...(!isAdded && { bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }) }}
                >
                  {isAdded ? 'REMOVE' : 'ADD'}
                </Button>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmLoc)} onClose={() => setConfirmLoc(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Remove Location</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Remove <strong>{confirmLoc}</strong> from your location priority list?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmLoc(null)} size="small">Cancel</Button>
          <Button onClick={() => { if (confirmLoc) onRemove(confirmLoc); setConfirmLoc(null); }} variant="contained" size="small" color="error">Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OrderTableRow
// ---------------------------------------------------------------------------

interface OrderTableRowProps {
  item: OrderItem;
  usePtr: boolean;
  idx: number;
  onQtyChange: (key: string, rawQty: number) => void;
  onRemove: (key: string) => void;
  readOnly?: boolean;
  focusQty?: boolean;
  onQtyEnter?: () => void;
  onAddSingleToCart?: (key: string) => void;
}

function OrderTableRow({ item, usePtr, idx, onQtyChange, onRemove, readOnly, focusQty, onQtyEnter, onAddSingleToCart }: OrderTableRowProps) {
  const p = item.product;
  const { paid, free: schemeFree } = computeSchemeBreakdown(item.qty, p.scheme);
  const free = p.scheme ? schemeFree : (item.freeQty ?? schemeFree);
  const rate = usePtr ? (p.ptr ?? p.approxRate ?? p.mrp ?? 0) : (p.pts ?? p.approxRate ?? p.mrp ?? 0);
  const [localVal, setLocalVal] = useState(String(paid));
  const { settings: rowAppSettings } = useAppSettings();
  const qtyInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusQty && qtyInputRef.current) {
      qtyInputRef.current.focus();
    }
  }, [focusQty]);

  useEffect(() => {
    const { paid: newPaid } = computeSchemeBreakdown(item.qty, p.scheme);
    setLocalVal(String(newPaid));
  }, [item.qty, p.scheme]);

  function handleBlur() {
    const parsed = parseFloat(localVal);
    let rawQty = (!isNaN(parsed) && parsed > 0) ? Math.floor(parsed) : 1;
    rawQty = capQtyToAvailableWithScheme(rawQty, p.scheme, p.availableQty, p.stockHidden);
    const { paid: newPaid } = computeSchemeBreakdown(rawQty, p.scheme);
    setLocalVal(String(newPaid));
    onQtyChange(item.key, rawQty);
  }

  return (
    <tr style={{ backgroundColor: 'transparent' }}>
      <td style={{ ...tblCell, textAlign: 'center', color: '#999' }}>{idx + 1}</td>
      <td style={tblCell}>
        <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.name}
        </div>
        <div style={{ fontSize: 11, color: '#1E3A8A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[
            [p.brand, p.packSize].filter(Boolean).join(' · '),
            formatNearExpiry(p.nearExpiry) ? `NE: ${formatNearExpiry(p.nearExpiry)}` : null,
          ].filter(Boolean).join(', ')}
        </div>
      </td>
      <td style={tblCell}>
        <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.distributorName}
        </div>
        {(p.distributorArea || p.distributorCity) && (
          <div style={{ fontSize: 10, color: '#1E3A8A' }}>{[p.distributorArea, p.distributorCity].filter(Boolean).join(', ')}</div>
        )}
      </td>
      <td style={{ ...tblCell, color: p.stockHidden ? '#999' : stockColor(p.availableQty, rowAppSettings.lowStockThreshold), fontWeight: 600 }}>
        {p.stockHidden ? 'Avail' : (p.availableQty ?? '—')}
      </td>
      <td style={tblCell}>
        {readOnly ? (
          <Typography sx={{ fontSize: 12, textAlign: 'center' }}>{paid}</Typography>
        ) : (
          <TextField
            inputRef={qtyInputRef}
            value={localVal}
            onChange={e => setLocalVal(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={e => {
              if (e.key === 'Enter') { handleBlur(); onQtyEnter?.(); }
              e.stopPropagation();
            }}
            size="small"
            inputProps={{
              style: { padding: '2px 4px', fontSize: 12, textAlign: 'center' },
              onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
            }}
            sx={{
              width: 60,
              '& .MuiOutlinedInput-root': { height: 24 },
            }}
          />
        )}
      </td>
      <td style={{ ...tblCell, textAlign: 'center', fontSize: 11, fontWeight: 600, color: free > 0 ? '#15803d' : '#aaa' }}>
        {free > 0 ? `+${free}` : '—'}
      </td>
      <td style={{ ...tblCell, fontSize: 11, fontWeight: 600, color: p.scheme ? '#15803d' : '#aaa' }}>
        {p.scheme ?? '—'}
      </td>
      <td style={tblCell}>
        {p.mrp != null ? `₹${Number(p.mrp).toFixed(2)}` : '—'}
      </td>
      <td style={{ ...tblCell, fontWeight: 600 }}>
        {!p.priceHidden
          ? `₹${rate.toFixed(2)}`
          : (<>
              <div style={{ fontSize: 10, color: '#888', fontStyle: 'italic', fontWeight: 400 }}>Not available</div>
              {(p.approxRate ?? 0) > 0 && (
                <div style={{ fontSize: 11, color: '#888' }}>~₹{p.approxRate!.toFixed(2)} <span style={{ fontSize: 9 }}>(approx)</span></div>
              )}
            </>)}
      </td>
      <td style={{ ...tblCell, fontWeight: 700, color: '#152346' }}>
        {!p.priceHidden ? `₹${(rate * paid).toFixed(2)}` : (p.approxRate ?? 0) > 0 ? `~₹${(p.approxRate! * paid).toFixed(2)}` : '—'}
      </td>
      {!readOnly && (
        <td style={{ ...tblCell, textAlign: 'center' }}>
          <Tooltip title="Add this item to cart">
            <IconButton onClick={() => onAddSingleToCart?.(item.key)}
              sx={{ width: 24, height: 24, padding: 0, color: '#7c3aed', '&:hover': { bgcolor: '#ede9fe' } }}>
              <ShoppingCartIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </td>
      )}
      {!readOnly && (
        <td style={{ ...tblCell, textAlign: 'center' }}>
          <IconButton size="small" color="error" onClick={() => onRemove(item.key)}>
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </td>
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// OrderTable
// ---------------------------------------------------------------------------

interface OrderTableProps {
  items: OrderItem[];
  usePtr: boolean;
  onQtyChange: (key: string, qty: number) => void;
  onRemove: (key: string) => void;
  readOnly?: boolean;
  focusQtyKey?: string | null;
  onQtyEnter?: () => void;
  distFilter?: number | 'all';
  onDistFilterChange?: (filter: number | 'all') => void;
  onAddSingleToCart?: (key: string) => void;
  onAddSupplierToCart?: (distributorTenantId: number) => void;
}

function OrderTable({
  items, usePtr, onQtyChange, onRemove, readOnly, focusQtyKey, onQtyEnter,
  distFilter = 'all', onDistFilterChange = () => {}, onAddSingleToCart, onAddSupplierToCart,
}: OrderTableProps) {

  const distinctDists = useMemo(() => {
    const seen = new Map<number, string>();
    items.forEach(i => {
      if (!seen.has(i.product.distributorTenantId))
        seen.set(i.product.distributorTenantId, i.product.distributorName ?? String(i.product.distributorTenantId));
    });
    return seen;
  }, [items]);

  const displayed = distFilter === 'all' ? items : items.filter(i => i.product.distributorTenantId === distFilter);

  if (items.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', py: 6 }}>
        <Typography variant="body2">Search and select products to add them here.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Distributor filter — only shown when order has items from multiple distributors */}
      {!readOnly && distinctDists.size > 1 && (
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', mr: 0.5 }}>FILTER:</Typography>
          <Box
            component="button"
            onClick={() => onDistFilterChange('all')}
            sx={{
              fontSize: 11, px: 1, py: 0.25, borderRadius: 1, border: '1px solid',
              borderColor: distFilter === 'all' ? '#152346' : '#e0e0e0',
              bgcolor: distFilter === 'all' ? '#152346' : 'transparent',
              color: distFilter === 'all' ? '#fff' : '#374151',
              cursor: 'pointer', fontWeight: distFilter === 'all' ? 700 : 400,
            }}
          >
            All
          </Box>
          {Array.from(distinctDists.entries()).map(([id, name]) => (
            <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Box
                component="button"
                onClick={() => onDistFilterChange(distFilter === id ? 'all' : id)}
                sx={{
                  fontSize: 11, px: 1, py: 0.25, borderRadius: 1, border: '1px solid',
                  borderColor: distFilter === id ? '#152346' : '#e0e0e0',
                  bgcolor: distFilter === id ? '#152346' : 'transparent',
                  color: distFilter === id ? '#fff' : '#374151',
                  cursor: 'pointer', fontWeight: distFilter === id ? 700 : 400, whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Box>
              {onAddSupplierToCart && (
                <Tooltip title={`Add all ${name} items to cart`}>
                  <IconButton
                    onClick={() => onAddSupplierToCart(id)}
                    sx={{ width: 22, height: 18, minWidth: 0, minHeight: 16, padding: 0, bgcolor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '5px', '&:hover': { bgcolor: '#ddd6fe', borderColor: '#a78bfa' } }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 12, color: '#7c3aed' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <table style={{ width: '100%', minWidth: 960, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: '26%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: 52 }} />
            <col style={{ width: 68 }} />
            <col style={{ width: 48 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 72 }} />
            <col style={{ width: 84 }} />
            {!readOnly && <col style={{ width: 44 }} />}
            {!readOnly && <col style={{ width: 44 }} />}
          </colgroup>
          <thead>
            <tr>
              {['#', 'PRODUCT', 'SUPPLIER', 'STOCK', 'QTY', 'FREE', 'SCHEME', 'MRP', 'PRICE', 'TOTAL', ...(readOnly ? [] : ['', ''])].map((h, i) => (
                <th key={`${h}-${i}`} style={{ ...tblHead, textAlign: h === '#' || h === '' ? 'center' : 'left' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((item, idx) => (
              <OrderTableRow
                key={item.key}
                item={item}
                usePtr={usePtr}
                idx={idx}
                onQtyChange={onQtyChange}
                onRemove={onRemove}
                readOnly={readOnly}
                focusQty={item.key === focusQtyKey}
                onQtyEnter={onQtyEnter}
                onAddSingleToCart={onAddSingleToCart}
              />
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// SaveDraftDialog
// ---------------------------------------------------------------------------

function SaveTemplateDialog({
  open,
  onClose,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onSave(name.trim());
      setName('');
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      setError(status === 409
        ? 'A template with this name already exists. Please choose a different name.'
        : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    setName('');
    setError('');
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Save as Template</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Template Name"
          placeholder="e.g. Weekly Order – May 2025"
          value={name}
          onChange={e => { setName(e.target.value); setError(''); }}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
          error={!!error}
          helperText={error}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={handleClose} size="small" disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!name.trim() || saving}
          sx={{ bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }}
        >
          {saving ? 'Saving…' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// OrderBottomBar — shared action bar for By Product and By Distributor tabs
// ---------------------------------------------------------------------------

interface OrderBottomBarProps {
  orderItems: OrderItem[];
  usePtr: boolean;
  onSaveTemplate?: () => void;
  onUpdateTemplate?: () => void;
  onAddToCart: () => void;
}

function OrderBottomBar({ orderItems, usePtr, onSaveTemplate, onUpdateTemplate, onAddToCart }: OrderBottomBarProps) {
  const totalAmount = orderItems.reduce((sum, i) => {
    const rate = usePtr
      ? (i.product.ptr ?? i.product.approxRate ?? i.product.mrp ?? 0)
      : (i.product.pts ?? i.product.approxRate ?? i.product.mrp ?? 0);
    const { paid } = computeSchemeBreakdown(i.qty, i.product.scheme);
    return sum + rate * paid;
  }, 0);

  const distinctDistributors = new Set(orderItems.map(i => i.product.distributorTenantId)).size;

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, flex: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
            PRODUCTS
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14 }}>
            {orderItems.length}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
            SUPPLIERS
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14 }}>
            {distinctDistributors}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, display: 'block' }}>
            TOTAL
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 14, color: '#152346' }}>
            ₹{totalAmount.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {onUpdateTemplate && (
          <Button
            variant="contained"
            size="small"
            disabled={orderItems.length === 0}
            onClick={onUpdateTemplate}
            sx={{ fontSize: 11, whiteSpace: 'nowrap', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
          >
            UPDATE TEMPLATE
          </Button>
        )}
        {onSaveTemplate && (
          <Button
            variant="outlined"
            size="small"
            disabled={orderItems.length === 0}
            onClick={onSaveTemplate}
            sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
          >
            SAVE TEMPLATE
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<ShoppingCartIcon />}
          disabled={orderItems.length === 0}
          onClick={onAddToCart}
          sx={{
            fontSize: 11,
            whiteSpace: 'nowrap',
            bgcolor: '#7B61FF',
            '&:hover': { bgcolor: '#6a50ee' },
          }}
        >
          ADD TO CART
        </Button>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DistributorSearchBox — type-ahead distributor picker
// ---------------------------------------------------------------------------

interface DistributorSearchBoxProps {
  allDistributors: PeerTenant[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}

function DistributorSearchBox({ allDistributors, selectedId, onSelect }: DistributorSearchBoxProps) {
  const selected = allDistributors.find(d => d.id === selectedId) ?? null;
  const [query, setQuery] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Sync display text when selection changes externally
  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.name]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) {
        setOpen(false);
        // If no selection was made, restore displayed name or clear
        setQuery(selected?.name ?? '');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [selected?.name]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allDistributors;
    return allDistributors.filter(d =>
      d.name.toLowerCase().includes(q) ||
      (d.city ?? '').toLowerCase().includes(q) ||
      (d.district ?? '').toLowerCase().includes(q),
    );
  }, [allDistributors, query]);

  function handleSelect(d: PeerTenant) {
    onSelect(d.id);
    setQuery(d.name);
    setOpen(false);
  }

  function handleClear() {
    onSelect(null);
    setQuery('');
    setOpen(false);
  }

  return (
    <Box ref={boxRef} sx={{ position: 'relative', width: 240, flexShrink: 0 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search distributor…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><StoreIcon sx={{ fontSize: 15, color: '#9ca3af' }} /></InputAdornment>,
          endAdornment: selectedId ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
      />
      {open && filtered.length > 0 && (
        <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, maxHeight: 260, overflowY: 'auto', mt: 0.5 }}>
          {filtered.map(d => (
            <Box
              key={d.id}
              onClick={() => handleSelect(d)}
              sx={{
                px: 1.5, py: 1, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                bgcolor: d.id === selectedId ? '#eff6ff' : 'background.paper',
                '&:hover': { bgcolor: '#f0f4ff' },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#152346' }}>{d.name}</Typography>
              {(d.area || d.city) && (
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  {[d.area, d.city].filter(Boolean).join(', ')}
                </Typography>
              )}
              {d.gstin && (
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>GST: {d.gstin}</Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
      {open && filtered.length === 0 && query.trim() && (
        <Paper elevation={2} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, mt: 0.5, p: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No distributors match "{query}".</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RetailerSearchBox — type-ahead retailer picker
// ---------------------------------------------------------------------------

interface RetailerOption { id: number; name: string; area?: string; city?: string; gstin?: string; drugLicenseNumber?: string }

interface RetailerSearchBoxProps {
  retailers: RetailerOption[];
  selectedId: number | '';
  onSelect: (id: number | '', name: string) => void;
}

function retailerDisplayText(r: RetailerOption): string {
  return [r.name, r.area, r.city, r.gstin ? `GST: ${r.gstin}` : null].filter(Boolean).join(', ');
}

function RetailerSearchBox({ retailers, selectedId, onSelect }: RetailerSearchBoxProps) {
  const selected = selectedId !== '' ? retailers.find(r => r.id === selectedId) ?? null : null;
  const [query, setQuery] = useState(selected ? retailerDisplayText(selected) : '');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQuery(selected ? retailerDisplayText(selected) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery(selected ? retailerDisplayText(selected) : '');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return retailers;
    return retailers.filter(r => r.name.toLowerCase().includes(q));
  }, [retailers, query]);

  // Keep the first match highlighted whenever the result set changes.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filtered]);

  function handleSelect(r: RetailerOption) {
    onSelect(r.id, r.name);
    setQuery(retailerDisplayText(r));
    setOpen(false);
  }

  function handleClear() {
    onSelect('', '');
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { setOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = filtered[highlightedIndex];
      if (r) handleSelect(r);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery(selected ? retailerDisplayText(selected) : '');
    }
  }

  return (
    <Box ref={boxRef} sx={{ position: 'relative', minWidth: 340, maxWidth: 480, flex: 1 }}>
      <TextField
        size="small"
        fullWidth
        label="Select Retailer"
        placeholder="Search retailer…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); inputRef.current?.select(); }}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        InputProps={{
          endAdornment: selectedId !== '' ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } }}
      />
      {open && filtered.length > 0 && (
        <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, maxHeight: 280, overflowY: 'auto', mt: 0.5 }}>
          {filtered.map((r, idx) => (
            <Box
              key={r.id}
              onClick={() => handleSelect(r)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              sx={{
                px: 1.5, py: 1, cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                bgcolor: idx === highlightedIndex ? '#f0f4ff' : (r.id === selectedId ? '#eff6ff' : 'background.paper'),
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#152346' }}>{r.name}</Typography>
              {(r.area || r.city || r.gstin || r.drugLicenseNumber) && (
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  {[[r.area, r.city].filter(Boolean).join(', ') || null, r.gstin ? `GST: ${r.gstin}` : null, r.drugLicenseNumber ? `DL: ${r.drugLicenseNumber}` : null]
                    .filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
      {open && filtered.length === 0 && query.trim() && (
        <Paper elevation={2} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1400, mt: 0.5, p: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No retailers match "{query}".</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PlaceOrderTab  (merged By Product + By Distributor with a Select By control)
// ---------------------------------------------------------------------------

type StockSearchOption = 'ALL' | 'IN_STOCK';

interface PlaceOrderTabProps {
  allDistributors: PeerTenant[];
  allPeerCities: string[];
  priorityIds: number[];
  onReorder: (ids: number[]) => void;
  onRemove: (id: number) => void;
  onAdd: (id: number) => void;
  usePtr: boolean;
  orderItems: OrderItem[];
  onOrderItemsChange: (items: OrderItem[]) => void;
  onSaveTemplate: (name: string, items: OrderItem[]) => Promise<void>;
  editingDraftId?: string | null;
  editingDraftName?: string;
  onUpdateTemplate?: (items: OrderItem[]) => Promise<void>;
  onCancelEdit?: () => void;
  preferredCities: string[];
  onCityReorder: (cities: string[]) => void;
  onCityRemove: (city: string) => void;
  onCityAdd: (city: string) => void;
  defaultCity?: string;
  homeArea?: string;
  priorityLocations: string[];
  onLocationReorder: (locs: string[]) => void;
  onLocationRemove: (loc: string) => void;
  onLocationAdd: (loc: string) => void;
  /** Present only when the logged-in user is a REPRESENTATIVE. */
  isRep?: boolean;
  myRetailers?: { id: number; name: string; area?: string; city?: string; gstin?: string; drugLicenseNumber?: string }[];
  initialRetailerId?: number;
  initialRetailerName?: string;
}

function PlaceOrderTab({
  allDistributors, allPeerCities, priorityIds, onReorder, onRemove, onAdd,
  usePtr, orderItems, onOrderItemsChange, onSaveTemplate,
  editingDraftId, editingDraftName, onUpdateTemplate, onCancelEdit,
  preferredCities, onCityReorder, onCityRemove, onCityAdd, defaultCity, homeArea,
  priorityLocations, onLocationReorder, onLocationRemove, onLocationAdd,
  isRep, myRetailers, initialRetailerId, initialRetailerName,
}: PlaceOrderTabProps) {
  const [selectBy, setSelectBy] = useState<'product' | 'distributor'>('product');
  const [selectedDistributorId, setSelectedDistributorId] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [firstWordMatch, setFirstWordMatch] = useState(false);
  const [schemeOnly, setSchemeOnly] = useState(false);
  const [stockOption, setStockOption] = useState<StockSearchOption>('IN_STOCK');
  const [cityFilter, setCityFilter] = useState<'preferred' | 'all'>('preferred');
  const [openPanel, setOpenPanel] = useState<'dist' | 'city' | 'location' | null>(null);

  // Areas available from preferred-city distributors + own registered area
  const allPeerLocations = useMemo(() => {
    const s = new Set<string>();
    if (homeArea) s.add(homeArea);
    allDistributors.forEach(d => {
      const area = d.area ?? d.linkedTenantParty?.area;
      const city = d.city ?? d.linkedTenantParty?.city;
      if (area && (preferredCities.length === 0 || preferredCities.includes(city ?? '')))
        s.add(area);
    });
    return Array.from(s).sort();
  }, [allDistributors, preferredCities, homeArea]);
  const [searchResults, setSearchResults] = useState<CrossTenantProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [templateDlgOpen, setTemplateDlgOpen] = useState(false);
  const [cartMessage, setCartMessage] = useState('');
  const [minOrderDialog, setMinOrderDialog] = useState<{ open: boolean; violations: { name: string; total: number; minimum: number; cartTotal: number }[] }>({ open: false, violations: [] });
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Rep-mode: which retailer is this order being placed for
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | ''>(initialRetailerId ?? '');
  const [selectedRetailerName, setSelectedRetailerName] = useState(initialRetailerName ?? '');
  const cartSetRetailer = useCartStore((s) => s.setRetailer);

  // Sync initial retailer from navigation state (only on first mount)
  useEffect(() => {
    if (initialRetailerId) {
      setSelectedRetailerId(initialRetailerId);
      setSelectedRetailerName(initialRetailerName ?? '');
      cartSetRetailer(initialRetailerId, initialRetailerName);
    }
    // Clear retailer from cart on unmount
    return () => { cartSetRetailer(undefined, undefined); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep cart store in sync whenever the rep changes the selected retailer
  useEffect(() => {
    if (isRep) {
      cartSetRetailer(
        selectedRetailerId !== '' ? (selectedRetailerId as number) : undefined,
        selectedRetailerName || undefined,
      );
    }
  }, [isRep, selectedRetailerId, selectedRetailerName, cartSetRetailer]);

  const cartAddItems = useCartStore((s) => s.addItems);
  const cartItems = useCartStore((s) => s.items);
  const { settings: appSettings } = useAppSettings();
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [qtyFocusKey, setQtyFocusKey] = useState<string | null>(null);
  // Supplier filter for the order table below — lifted up from OrderTable so
  // "ADD TO CART" can scope itself to whichever supplier is currently filtered.
  const [distFilter, setDistFilter] = useState<number | 'all'>('all');
  useEffect(() => {
    if (distFilter !== 'all' && !orderItems.some(i => i.product.distributorTenantId === distFilter)) {
      setDistFilter('all');
    }
  }, [orderItems, distFilter]);

  // allPeerCities comes from the parent (all wholesaler/distributor cities across all peers)
  const allCities = allPeerCities;

  function togglePanel(panel: 'dist' | 'city' | 'location') {
    setOpenPanel(prev => (prev === panel ? null : panel));
  }

  // Reset search when mode or distributor selection changes
  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  }, [selectBy, selectedDistributorId]);

  const doSearch = useCallback(
    async (q: string) => {
      setSearching(true);
      setSearchError('');
      try {
        let ids: number[];
        if (selectBy === 'product') {
          const allIds = allDistributors.map(d => d.id);
          if (allIds.length === 0) { setSearchResults([]); setShowDropdown(false); return; }
          const nonPriority = allIds.filter(id => !priorityIds.includes(id));
          ids = [...priorityIds, ...nonPriority];
        } else {
          if (!selectedDistributorId) { setSearchResults([]); return; }
          ids = [selectedDistributorId as number];
        }
        const repRetailerId = isRep && selectedRetailerId !== '' ? (selectedRetailerId as number) : undefined;
        const results = await searchCrossTenantProducts(ids, q, repRetailerId);
        let filtered = results;
        if (firstWordMatch && q.trim()) {
          const fw = q.trim().toLowerCase().split(/\s+/)[0];
          filtered = filtered.filter(r => r.name.toLowerCase().startsWith(fw));
        }
        if (schemeOnly) filtered = filtered.filter(r => Boolean(r.scheme));
        if (stockOption === 'IN_STOCK') filtered = filtered.filter(r => r.stockHidden || (r.availableQty ?? 0) > 0);

        // 3-tier sort in product mode: supplier priority → preferred city → location priority
        if (selectBy === 'product') {
          const priorityIdSet = new Set(priorityIds);
          if (cityFilter === 'preferred' && preferredCities.length > 0) {
            filtered = filtered.filter(r =>
              priorityIdSet.has(r.distributorTenantId) ||
              (r.distributorCity && preferredCities.includes(r.distributorCity)));
          }
          const areaById = new Map(allDistributors.map(d => [d.id, d.area ?? d.linkedTenantParty?.area ?? '']));
          filtered = [...filtered].sort((a, b) => {
            // Tier 1: Explicit supplier priority (not fetch-order rank)
            const aInPri = priorityIdSet.has(a.distributorTenantId);
            const bInPri = priorityIdSet.has(b.distributorTenantId);
            if (aInPri !== bInPri) return aInPri ? -1 : 1;
            if (aInPri && bInPri) {
              const aSupScore = a.priorityRank ?? 99999;
              const bSupScore = b.priorityRank ?? 99999;
              if (aSupScore !== bSupScore) return aSupScore - bSupScore;
            }
            // Tier 2: City priority rank
            const aCityRank = a.distributorCity ? preferredCities.indexOf(a.distributorCity) : -1;
            const bCityRank = b.distributorCity ? preferredCities.indexOf(b.distributorCity) : -1;
            const aCityScore = aCityRank >= 0 ? aCityRank : 99999;
            const bCityScore = bCityRank >= 0 ? bCityRank : 99999;
            if (aCityScore !== bCityScore) return aCityScore - bCityScore;
            // Tier 3: Area priority rank
            const aDistrict = areaById.get(a.distributorTenantId) ?? '';
            const bDistrict = areaById.get(b.distributorTenantId) ?? '';
            const aLocRank = aDistrict ? priorityLocations.indexOf(aDistrict) : -1;
            const bLocRank = bDistrict ? priorityLocations.indexOf(bDistrict) : -1;
            return (aLocRank >= 0 ? aLocRank : 99999) - (bLocRank >= 0 ? bLocRank : 99999);
          });
        }

        setSearchResults(filtered);
        setShowDropdown(true);
      } catch {
        setSearchError('Failed to search products. Please try again.');
      } finally {
        setSearching(false);
      }
    },
    [selectBy, allDistributors, priorityIds, selectedDistributorId, firstWordMatch, schemeOnly, stockOption, preferredCities, cityFilter, priorityLocations, isRep, selectedRetailerId],
  );

  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) { setSearchResults([]); setShowDropdown(false); return; }
    if (selectBy === 'distributor' && !selectedDistributorId) { setSearchResults([]); return; }
    if (isRep && !selectedRetailerId) { setSearchResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void doSearch(searchQuery), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, selectBy, selectedDistributorId, isRep, selectedRetailerId, doSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!searchBoxRef.current?.contains(e.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlight whenever results refresh
  useEffect(() => { setHighlightedIndex(searchResults.length > 0 ? 0 : -1); }, [searchResults]);

  // Scroll highlighted row into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const row = dropdownRef.current.querySelector<HTMLElement>(`[data-idx="${highlightedIndex}"]`);
      row?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  function addToOrder(product: CrossTenantProduct) {
    const existing = orderItems.find(
      i => i.product.productId === product.productId && i.product.distributorTenantId === product.distributorTenantId,
    );
    let focusKey: string;
    if (existing) {
      const currentRaw = Math.floor(existing.qty);
      const { paid } = computeSchemeBreakdown(currentRaw + 1, existing.product.scheme);
      onOrderItemsChange(orderItems.map(i => (i.key === existing.key ? { ...i, qty: paid } : i)));
      focusKey = existing.key;
    } else {
      const key = `${product.distributorTenantId}-${product.productId}-${Date.now()}`;
      const { paid } = computeSchemeBreakdown(1, product.scheme);
      onOrderItemsChange([...orderItems, { key, product, qty: paid }]);
      focusKey = key;
    }
    setShowDropdown(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    setQtyFocusKey(focusKey);
  }

  const [confirmRemoveKey, setConfirmRemoveKey] = useState<string | null>(null);

  function removeItem(key: string) { onOrderItemsChange(orderItems.filter(i => i.key !== key)); }
  function changeQty(key: string, rawQty: number) {
    const item = orderItems.find(i => i.key === key);
    if (!item) return;
    const { paid } = computeSchemeBreakdown(rawQty, item.product.scheme);
    onOrderItemsChange(orderItems.map(i => i.key === key ? { ...i, qty: paid } : i));
  }

  /**
   * Adds a specific subset of orderItems to the cart (checking min-order
   * amounts per distributor for just that subset) and removes only those
   * items from the staged list — everything else stays put. Used by the
   * main "ADD TO CART" button (scoped to the active supplier filter), the
   * per-row quick-add, and the per-supplier "cart all" shortcut.
   */
  function addItemsToCart(itemsToAdd: OrderItem[]) {
    if (itemsToAdd.length === 0) return;

    // Group by distributor and check min order amount per distributor
    const byDist = new Map<number, { items: OrderItem[]; name: string; minOrderAmount: number }>();
    for (const item of itemsToAdd) {
      const distId = item.product.distributorTenantId;
      if (!byDist.has(distId)) {
        byDist.set(distId, {
          items: [],
          name: item.product.distributorName,
          minOrderAmount: item.product.minOrderAmount ?? 0,
        });
      }
      byDist.get(distId)!.items.push(item);
    }

    const violations: { name: string; total: number; minimum: number; cartTotal: number }[] = [];
    for (const [distId, dist] of byDist) {
      if (dist.minOrderAmount <= 0) continue;
      const itemsTotal = dist.items.reduce((sum, i) => {
        const rate = usePtr ? (i.product.ptr ?? i.product.approxRate ?? i.product.mrp ?? 0) : (i.product.pts ?? i.product.approxRate ?? i.product.mrp ?? 0);
        return sum + rate * i.qty;
      }, 0);
      const cartTotal = cartItems
        .filter(i => i.product.distributorTenantId === distId)
        .reduce((sum, i) => {
          const rate = usePtr ? (i.product.ptr ?? i.product.approxRate ?? i.product.mrp ?? 0) : (i.product.pts ?? i.product.approxRate ?? i.product.mrp ?? 0);
          return sum + rate * i.qty;
        }, 0);
      const total = itemsTotal + cartTotal;
      if (total < dist.minOrderAmount) {
        violations.push({ name: dist.name, total, minimum: dist.minOrderAmount, cartTotal });
      }
    }

    if (violations.length > 0) {
      setMinOrderDialog({ open: true, violations });
      return;
    }

    cartAddItems(itemsToAdd);
    setCartMessage(`${itemsToAdd.length} item${itemsToAdd.length !== 1 ? 's' : ''} added to cart!`);
    const addedKeys = new Set(itemsToAdd.map(i => i.key));
    onOrderItemsChange(orderItems.filter(i => !addedKeys.has(i.key)));
  }

  /** Main "ADD TO CART" button — scoped to whichever supplier filter is active in the table (or everything, if none). */
  function handleAddToCart() {
    const scoped = distFilter === 'all' ? orderItems : orderItems.filter(i => i.product.distributorTenantId === distFilter);
    addItemsToCart(scoped);
  }

  /** Per-row quick-add — carts just that one item. */
  function handleAddSingleToCart(key: string) {
    const item = orderItems.find(i => i.key === key);
    if (item) addItemsToCart([item]);
  }

  /** Per-supplier "cart all" shortcut next to a filter chip. */
  function handleAddSupplierToCart(distributorTenantId: number) {
    addItemsToCart(orderItems.filter(i => i.product.distributorTenantId === distributorTenantId));
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Far-left icon strip (always visible) ── */}
      <Box
        sx={{
          width: 36,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f3f4f6',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0.75,
          gap: 0.5,
        }}
      >
        <Tooltip title="Supplier Priority (Tier 1)" placement="right">
          <IconButton
            size="small"
            onClick={() => togglePanel('dist')}
            sx={{
              borderRadius: 1,
              bgcolor: openPanel === 'dist' ? '#152346' : 'transparent',
              color: openPanel === 'dist' ? '#fff' : '#6b7280',
              '&:hover': { bgcolor: openPanel === 'dist' ? '#1e3460' : 'action.hover' },
            }}
          >
            <StoreIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Preferred Cities" placement="right">
          <IconButton
            size="small"
            onClick={() => togglePanel('city')}
            sx={{
              borderRadius: 1,
              bgcolor: openPanel === 'city' ? '#152346' : 'transparent',
              color: openPanel === 'city' ? '#fff' : '#6b7280',
              '&:hover': { bgcolor: openPanel === 'city' ? '#1e3460' : 'action.hover' },
            }}
          >
            <LocationOnIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Area Priority (Tier 3)" placement="right">
          <IconButton
            size="small"
            onClick={() => togglePanel('location')}
            sx={{
              borderRadius: 1,
              bgcolor: openPanel === 'location' ? '#152346' : 'transparent',
              color: openPanel === 'location' ? '#fff' : '#6b7280',
              '&:hover': { bgcolor: openPanel === 'location' ? '#1e3460' : 'action.hover' },
            }}
          >
            <MapIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Collapsible panel (Distributor or City) ── */}
      <Box
        sx={{
          width: openPanel ? 220 : 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width 0.2s ease',
          borderRight: openPanel ? '1px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.50',
        }}
      >
        {openPanel === 'dist' && (
          <DistributorSidebar
            allDistributors={allDistributors}
            priorityIds={priorityIds}
            onReorder={onReorder}
            onRemove={onRemove}
            onAdd={onAdd}
          />
        )}
        {openPanel === 'city' && (
          <CitySidebar
            allCities={allCities}
            preferredCities={preferredCities}
            defaultCity={defaultCity}
            onReorder={onCityReorder}
            onRemove={onCityRemove}
            onAdd={onCityAdd}
          />
        )}
        {openPanel === 'location' && (
          <LocationSidebar
            allLocations={allPeerLocations}
            priorityLocations={priorityLocations}
            onReorder={onLocationReorder}
            onRemove={onLocationRemove}
            onAdd={onLocationAdd}
          />
        )}
      </Box>

      {/* ── Search options (fixed left panel) ── */}
      <Box
        sx={{
          width: 175,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f9fafb',
          px: 1.5,
          py: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto',
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5 }}>
          SEARCH OPTIONS
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[
            { label: 'Match from Beginning', checked: firstWordMatch, set: () => setFirstWordMatch(v => !v) },
            { label: 'Scheme Items Only', checked: schemeOnly, set: () => setSchemeOnly(v => !v) },
          ].map(o => (
            <Box key={o.label} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
              <input type="checkbox" checked={o.checked} onChange={o.set} style={{ width: 12, height: 12 }} />
              <Typography sx={{ fontSize: 11, color: '#374151', userSelect: 'none' }}>{o.label}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5, mt: 0.5 }}>
          STOCK FILTER
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {([
            { value: 'IN_STOCK', label: 'In Stock Only' },
            { value: 'ALL', label: 'All' },
          ] as { value: StockSearchOption; label: string }[]).map(o => (
            <Box key={o.value} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
              <input type="radio" name="po-stock" checked={stockOption === o.value} onChange={() => setStockOption(o.value)} style={{ width: 12, height: 12 }} />
              <Typography sx={{ fontSize: 11, color: '#374151', userSelect: 'none' }}>{o.label}</Typography>
            </Box>
          ))}
        </Box>
        {selectBy === 'product' && preferredCities.length > 0 && (
          <>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: 0.5, mt: 0.5 }}>
              CITY FILTER
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {([
                { value: 'preferred', label: 'Preferred Cities' },
                { value: 'all', label: 'All Cities' },
              ] as { value: 'preferred' | 'all'; label: string }[]).map(o => (
                <Box key={o.value} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
                  <input type="radio" name="po-city" checked={cityFilter === o.value} onChange={() => setCityFilter(o.value)} style={{ width: 12, height: 12 }} />
                  <Typography sx={{ fontSize: 11, color: '#374151', userSelect: 'none' }}>{o.label}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Rep-mode: Retailer selector banner */}
        {isRep && (
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', gap: 1.5, alignItems: 'center', bgcolor: '#f5f3ff' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#4c1d95', flexShrink: 0 }}>
              Ordering for:
            </Typography>
            <RetailerSearchBox
              retailers={myRetailers ?? []}
              selectedId={selectedRetailerId}
              onSelect={(id, name) => {
                setSelectedRetailerId(id);
                setSelectedRetailerName(name);
                // Clear search results when retailer changes
                setSearchQuery('');
                setSearchResults([]);
                setShowDropdown(false);
              }}
            />
            {selectedRetailerName && (
              <Typography sx={{ fontSize: 11, color: '#6d28d9' }}>
                Products shown are from suppliers that have added this retailer.
              </Typography>
            )}
            {!selectedRetailerId && (
              <Typography sx={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                Select a retailer first to search products.
              </Typography>
            )}
          </Box>
        )}

        {/* Top bar: Search By | Distributor combo | Search textbox */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
            <InputLabel sx={{ fontSize: 13 }}>Search By</InputLabel>
            <Select
              value={selectBy}
              onChange={e => setSelectBy(e.target.value as 'product' | 'distributor')}
              label="Search By"
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="product">Product</MenuItem>
              <MenuItem value="distributor">Supplier</MenuItem>
            </Select>
          </FormControl>

          {selectBy === 'distributor' && (
            <DistributorSearchBox
              allDistributors={allDistributors}
              selectedId={selectedDistributorId as number | null}
              onSelect={id => setSelectedDistributorId(id ?? '')}
            />
          )}

          {/* Search textbox */}
          <div ref={searchBoxRef} style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                isRep && !selectedRetailerId
                  ? 'Select a retailer above first…'
                  : selectBy === 'product'
                    ? (allDistributors.length === 0 ? 'No suppliers available…' : 'Search & Add Products')
                    : (!selectedDistributorId ? 'Select a supplier first…' : 'Search products from this supplier…')
              }
              inputRef={searchInputRef}
              value={searchQuery}
              disabled={(isRep && !selectedRetailerId) || (selectBy === 'product' ? allDistributors.length === 0 : !selectedDistributorId)}
              onChange={e => { setSearchQuery(e.target.value); setHighlightedIndex(-1); }}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              onKeyDown={e => {
                if (!showDropdown || searchResults.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex(i => (i < searchResults.length - 1 ? i + 1 : i));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex(i => (i > 0 ? i - 1 : 0));
                } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                  e.preventDefault();
                  addToOrder(searchResults[highlightedIndex]);
                } else if (e.key === 'Escape') {
                  setShowDropdown(false);
                  setHighlightedIndex(-1);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
            />

            {/* Dropdown results */}
            {showDropdown && searchResults.length > 0 && (
              <Paper ref={dropdownRef} elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, maxHeight: 320, overflowY: 'auto', mt: 0.5 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['PRODUCT', ...(selectBy === 'product' ? ['SUPPLIER'] : []), 'STOCK', 'Near Exp', 'MRP', usePtr ? 'PTR' : 'PTS', 'SCHEME'].map(h => (
                        <th key={h} style={{ ...tblHead, position: 'sticky', top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((r, idx) => {
                      const rate = usePtr ? r.ptr : r.pts;
                      const isHighlighted = idx === highlightedIndex;
                      const rowBg = isHighlighted ? '#e0eaff' : (r.stockHidden ? 'transparent' : stockRowBg(r.availableQty, appSettings.lowStockThreshold));
                      return (
                        <tr
                          key={`${r.distributorTenantId}-${r.productId}`}
                          data-idx={idx}
                          onClick={() => addToOrder(r)}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          onMouseLeave={() => setHighlightedIndex(-1)}
                          style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0', backgroundColor: rowBg }}
                        >
                          <td style={tblCell}>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>{r.name}</div>
                            <div style={{ fontSize: 10, color: '#888' }}>{[r.brand, r.packSize, r.uom].filter(Boolean).join(' · ')}</div>
                          </td>
                          {selectBy === 'product' && (
                            <td style={tblCell}>
                              <div style={{ fontSize: 11 }}>{r.distributorName}</div>
                              {(r.distributorArea || r.distributorCity) && (
                                <div style={{ fontSize: 10, color: '#888' }}>{[r.distributorArea, r.distributorCity].filter(Boolean).join(', ')}</div>
                              )}
                            </td>
                          )}
                          <td style={{ ...tblCell, color: r.stockHidden ? '#999' : stockColor(r.availableQty, appSettings.lowStockThreshold), fontWeight: 600 }}>
                            {r.stockHidden ? 'Avail' : (r.availableQty ?? '—')}
                          </td>
                          <td style={{ ...tblCell, fontSize: 11, color: '#888' }}>
                            {formatNearExpiry(r.nearExpiry) ?? '—'}
                          </td>
                          <td style={{ ...tblCell, fontSize: 12 }}>
                            {r.mrp != null ? `₹${r.mrp}` : '—'}
                          </td>
                          <td style={{ ...tblCell, fontSize: 12, fontWeight: 600 }}>
                            {!r.priceHidden && rate != null
                              ? `₹${rate}`
                              : (<>
                                  <div style={{ fontSize: 10, color: '#888', fontWeight: 400, fontStyle: 'italic' }}>Not available</div>
                                  {r.approxRate != null && (
                                    <>
                                      <span style={{ fontSize: 11 }}>~₹{r.approxRate}</span>
                                      <div style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>(approx PTR)</div>
                                    </>
                                  )}
                                </>)}
                          </td>
                          <td style={{ ...tblCell, fontSize: 11, fontWeight: (!r.schemeHidden && r.scheme) ? 600 : 400, color: (!r.schemeHidden && r.scheme) ? '#15803d' : '#aaa' }}>{(!r.schemeHidden && r.scheme) ? r.scheme : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Paper>
            )}
            {showDropdown && searchQuery.trim() && !searching && searchResults.length === 0 && (
              <Paper elevation={2} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, mt: 0.5, p: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                  No products found for "{searchQuery}".
                </Typography>
              </Paper>
            )}
          </div>

          {searchError && (
            <Typography sx={{ fontSize: 11, color: 'error.main', flexShrink: 0 }}>
              {searchError}
            </Typography>
          )}
        </Box>

        {/* Editing mode banner */}
        {editingDraftId && (
          <Box sx={{ px: 2, py: 0.75, bgcolor: '#fff8e1', borderBottom: '1px solid #ffe082', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, color: '#5d4037', flex: 1 }}>
              ✏️ Editing template: <strong>{editingDraftName}</strong>
            </Typography>
            <Button size="small" sx={{ fontSize: 11 }} onClick={onCancelEdit}>Cancel</Button>
          </Box>
        )}

        {/* Order table + bottom bar */}
        <OrderTable items={orderItems} usePtr={usePtr} onQtyChange={changeQty} onRemove={key => setConfirmRemoveKey(key)}
          focusQtyKey={qtyFocusKey}
          onQtyEnter={() => { setQtyFocusKey(null); searchInputRef.current?.focus(); }}
          distFilter={distFilter}
          onDistFilterChange={setDistFilter}
          onAddSingleToCart={handleAddSingleToCart}
          onAddSupplierToCart={handleAddSupplierToCart}
        />
        <OrderBottomBar
          orderItems={orderItems}
          usePtr={usePtr}
          onSaveTemplate={editingDraftId ? undefined : () => setTemplateDlgOpen(true)}
          onUpdateTemplate={editingDraftId && onUpdateTemplate ? () => onUpdateTemplate(orderItems) : undefined}
          onAddToCart={handleAddToCart}
        />
      </Box>

      <SaveTemplateDialog
        open={templateDlgOpen}
        onClose={() => setTemplateDlgOpen(false)}
        onSave={async name => {
          await onSaveTemplate(name, orderItems);
          setTemplateDlgOpen(false);
        }}
      />

      <Snackbar
        open={Boolean(cartMessage)}
        autoHideDuration={4000}
        onClose={() => setCartMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCartMessage('')} severity="success" sx={{ width: '100%' }}>
          {cartMessage}
        </Alert>
      </Snackbar>

      {/* Min order amount violation dialog */}
      <Dialog open={minOrderDialog.open} onClose={() => setMinOrderDialog({ open: false, violations: [] })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Minimum Order Not Reached</DialogTitle>
        <DialogContent>
          {minOrderDialog.violations.map(v => (
            <Box key={v.name} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{v.name}</Typography>
              <Typography sx={{ fontSize: 12, color: '#374151' }}>
                Current order: ₹{(v.total - v.cartTotal).toFixed(2)}
                {v.cartTotal > 0 ? ` + Cart: ₹${v.cartTotal.toFixed(2)} = ₹${v.total.toFixed(2)}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#dc2626' }}>
                Minimum required: ₹{v.minimum.toFixed(2)} &nbsp;(₹{(v.minimum - v.total).toFixed(2)} more needed)
              </Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
            Please add more items from {minOrderDialog.violations.length === 1 ? 'this supplier' : 'these suppliers'} to meet the minimum order value.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setMinOrderDialog({ open: false, violations: [] })} variant="contained" size="small">OK</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm remove order item */}
      <Dialog open={confirmRemoveKey !== null} onClose={() => setConfirmRemoveKey(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Remove Product</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Remove <strong>{orderItems.find(i => i.key === confirmRemoveKey)?.product.name}</strong> from the order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmRemoveKey(null)} size="small">Cancel</Button>
          <Button
            onClick={() => { if (confirmRemoveKey) removeItem(confirmRemoveKey); setConfirmRemoveKey(null); }}
            variant="contained" size="small" color="error"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// [ByDistributorTab removed — merged into PlaceOrderTab above]
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// OrderTemplatesTab
// ---------------------------------------------------------------------------

interface OrderTemplatesTabProps {
  usePtr: boolean;
  drafts: DraftOrder[];
  onLoadTemplate: (draft: DraftOrder) => void;
  onEditTemplate: (draft: DraftOrder) => void;
  onRenameTemplate: (id: string, newName: string) => Promise<void>;
  onDeleteTemplate: (id: string) => void;
  onFetchItems: (id: string) => Promise<OrderItem[]>;
}

function OrderTemplatesTab({ usePtr, drafts, onLoadTemplate, onEditTemplate, onRenameTemplate, onDeleteTemplate, onFetchItems }: OrderTemplatesTabProps) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [previewItems, setPreviewItems] = useState<OrderItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const selectedDraft = drafts.find(d => d.id === selectedId);

  useEffect(() => {
    if (!selectedId) { setPreviewItems([]); return; }
    const draft = drafts.find(d => d.id === selectedId);
    if (!draft) { setPreviewItems([]); return; }
    if (draft.items.length > 0) { setPreviewItems(draft.items); return; }
    setPreviewLoading(true);
    onFetchItems(selectedId)
      .then(items => setPreviewItems(items))
      .catch(() => setPreviewItems([]))
      .finally(() => setPreviewLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {drafts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            No saved templates. Use <strong>SAVE TEMPLATE</strong> in the Place Order tab to save templates.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel sx={{ fontSize: 13 }}>Select Template</InputLabel>
              <Select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value as string)}
                label="Select Template"
                sx={{ fontSize: 13 }}
              >
                {drafts.map(d => (
                  <MenuItem key={d.id} value={d.id}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{d.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {(d.itemCount ?? d.items.length)} product{(d.itemCount ?? d.items.length) !== 1 ? 's' : ''} · saved{' '}
                        {new Date(d.savedAt).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDraft && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onLoadTemplate(selectedDraft)}
                  sx={{ fontSize: 11, bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' }, whiteSpace: 'nowrap' }}
                >
                  Load
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onEditTemplate(selectedDraft)}
                  sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                >
                  Edit Items
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setRenamingId(selectedDraft.id); setRenameValue(selectedDraft.name); setRenameError(''); }}
                  sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                >
                  Rename
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setConfirmDeleteId(selectedDraft.id)}
                  sx={{ fontSize: 11, whiteSpace: 'nowrap' }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Confirm delete template dialog */}
      <Dialog open={confirmDeleteId !== null} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Delete Template</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            Delete <strong>{drafts.find(d => d.id === confirmDeleteId)?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteId(null)} size="small">Cancel</Button>
          <Button
            onClick={() => {
              if (confirmDeleteId) { onDeleteTemplate(confirmDeleteId); setSelectedId(''); }
              setConfirmDeleteId(null);
            }}
            variant="contained" size="small" color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renamingId} onClose={() => setRenamingId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Rename Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="New Name"
            value={renameValue}
            onChange={e => { setRenameValue(e.target.value); setRenameError(''); }}
            onKeyDown={async e => {
              if (e.key === 'Enter' && renameValue.trim() && !renameSaving) {
                setRenameSaving(true);
                try {
                  await onRenameTemplate(renamingId!, renameValue.trim());
                  setRenamingId(null);
                } catch (err: unknown) {
                  setRenameError((err as { status?: number }).status === 409
                    ? 'A template with this name already exists.'
                    : 'Failed to rename. Please try again.');
                } finally { setRenameSaving(false); }
              }
            }}
            error={!!renameError} helperText={renameError} sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setRenamingId(null)} size="small" disabled={renameSaving}>Cancel</Button>
          <Button
            variant="contained" size="small"
            disabled={!renameValue.trim() || renameSaving}
            sx={{ bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }}
            onClick={async () => {
              setRenameSaving(true);
              setRenameError('');
              try {
                await onRenameTemplate(renamingId!, renameValue.trim());
                setRenamingId(null);
              } catch (err: unknown) {
                setRenameError((err as { status?: number }).status === 409
                  ? 'A template with this name already exists.'
                  : 'Failed to rename. Please try again.');
              } finally { setRenameSaving(false); }
            }}
          >
            {renameSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedDraft ? (
        previewLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading items…</Typography>
          </Box>
        ) : (
          <OrderTable
            items={previewItems}
            usePtr={usePtr}
            onQtyChange={() => {}}
            onRemove={() => {}}
            readOnly
          />
        )
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {drafts.length > 0 ? 'Select a template above to preview its items.' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OrderStatusTab — track placed orders by distributor / by product
// ---------------------------------------------------------------------------

const ORDER_STATUS_MAP: Record<OrderStatus, { label: string; bg: string }> = {
  DRAFT:               { label: 'Draft',     bg: '#9ca3af' },
  PLACED:              { label: 'New',       bg: '#f97316' },
  VIEWED:              { label: 'Viewed',    bg: '#6366f1' },
  CONFIRMED:           { label: 'Confirmed', bg: '#3b82f6' },
  PARTIALLY_FULFILLED: { label: 'Partial',   bg: '#f59e0b' },
  INVOICED:            { label: 'Billed',    bg: '#06b6d4' },
  DISPATCHED:          { label: 'Dispatched', bg: '#8b5cf6' },
  DELIVERED:           { label: 'Completed', bg: '#22c55e' },
  REJECTED:            { label: 'Rejected',  bg: '#ef4444' },
  CANCELLED:           { label: 'Cancelled', bg: '#B22222' },
};

const MY_ORDERS_FILTER_STATUSES: OrderStatus[] = ['PLACED', 'VIEWED', 'CONFIRMED', 'DELIVERED', 'REJECTED', 'CANCELLED'];

type TrackFilterMode = 'distributor' | 'product' | 'reports';
type QuickFilter = 'all' | 'schemes' | 'shortages' | 'rep';
type TrackDateFilter = 'ALL' | 'TODAY' | 'YESTERDAY' | 'THIS_WEEK' | 'THIS_MONTH';

/** YYYY-MM-DD from the browser's LOCAL calendar date — avoids the UTC day-shift `toISOString()` causes near local midnight. */
function toLocalTrackDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Converts the Time Period quick-filter into a start/end date pair (YYYY-MM-DD) sent to the backend. */
function computeTrackQuickDateRange(filter: TrackDateFilter): { startDate?: string; endDate?: string } {
  const todayStr = toLocalTrackDateStr(new Date());
  switch (filter) {
    case 'TODAY':
      return { startDate: todayStr, endDate: todayStr };
    case 'YESTERDAY': {
      const y = new Date(); y.setDate(y.getDate() - 1);
      const s = toLocalTrackDateStr(y);
      return { startDate: s, endDate: s };
    }
    case 'THIS_WEEK': {
      const now = new Date();
      const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
      return { startDate: toLocalTrackDateStr(weekStart), endDate: todayStr };
    }
    case 'THIS_MONTH': {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toLocalTrackDateStr(monthStart), endDate: todayStr };
    }
    default:
      return {};
  }
}

function formatTrackAmount(n: number) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatTrackTime(s: string) {
  return new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// Column widths mirror the Order Inbox grid: chevron | supplier+tag | phone | location | order# | value | spacer | actions | status
// Every data column has a 100px floor; the supplier-name column's floor is 150px (the chevron
// is icon-only utility, not data, so it stays small). The last column got a much bigger ceiling
// (260px, was 200px) so the status badge + all three action buttons (Repeat/Cancel-or-CheckItems/
// Download Invoice) fit on screen together without forcing horizontal scroll to see the last one.
const MY_ORDERS_COLS = '24px minmax(150px,260px) minmax(100px,110px) minmax(100px,120px) minmax(100px,auto) minmax(220px,260px)';

// Status and Actions are separate fixed-width tracks (not flex-shrunk to content) so every row's
// status badge and action buttons line up at the same x position regardless of how many action
// buttons that particular row happens to render. Kept tight so the actions column has room to
// show all three buttons within the widened last grid column.
const STATUS_COL_WIDTH = 80;
const ACTIONS_COL_WIDTH = 64;

function triggerTrackDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

interface TrackReportDef { id: string; name: string; description: string }
const TRACK_REPORT_DEFS: TrackReportDef[] = [
  { id: 'order-status',   name: 'Order Status Summary',  description: 'All my orders grouped and totalled by status' },
  { id: 'supplier-wise',  name: 'Supplier-wise Summary', description: 'Orders count and revenue per supplier / distributor' },
  { id: 'daily-orders',   name: 'Daily Orders Report',   description: 'Orders, items and revenue grouped by date' },
];

interface OrderStatusTabProps {
  initialView?: 'date' | 'supplier' | 'product';
  /** Lifted up to MyOrdersPage so the search box can render beside the Date/Supplier/Product Wise
   *  tabs instead of in this tab's own header — frees up vertical space for the order list. */
  appliedTrackSearch: { mode: 'supplier' | 'orderId' | 'product' | 'phone'; query: string } | null;
  appliedDateRange: { startDate?: string; endDate?: string } | null;
  clearTrackSearch: () => void;
  clearManualDateRange: () => void;
}

function OrderStatusTab({ initialView = 'date', appliedTrackSearch, appliedDateRange, clearTrackSearch, clearManualDateRange }: OrderStatusTabProps) {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const isRep = user?.tenantType === 'REPRESENTATIVE';

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [distributorNames, setDistributorNames] = useState<Map<number, string>>(new Map());
  const [distributorInfo, setDistributorInfo] = useState<Map<number, PeerTenant>>(new Map());
  const [viewSupplierParty, setViewSupplierParty] = useState<PeerTenant | null>(null);
  const [viewOutstandingParty, setViewOutstandingParty] = useState<PeerTenant | null>(null);
  /** For reps: maps retailerTenantId → retailer display name. */
  const [retailerNames, setRetailerNames] = useState<Map<number, string>>(new Map());
  const savedStatusTabFilters = loadSessionFilters<{
    filterMode: TrackFilterMode; quickFilter: QuickFilter; viewMode: 'date' | 'supplier' | 'product';
    selectedStatuses: OrderStatus[]; dateFilter: TrackDateFilter;
  }>('myOrdersStatusTab');
  const [filterMode, setFilterMode] = useState<TrackFilterMode>(() => savedStatusTabFilters.filterMode ?? 'distributor');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(() => savedStatusTabFilters.quickFilter ?? 'all');
  const [viewMode, setViewMode] = useState<'date' | 'supplier' | 'product'>(() => savedStatusTabFilters.viewMode ?? initialView);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(new Set());
  function toggleOrderExpanded(id: number) {
    setExpandedOrderIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  const [shortageStore, setShortageStore] = useState<Map<number, OrderShortageRecord>>(new Map());
  const [csvTargetOrder, setCsvTargetOrder] = useState<Order | null>(null);
  const [checkItemsDlg, setCheckItemsDlg] = useState(false);
  const [csvResultDlg, setCsvResultDlg] = useState(false);
  const [csvResultShortages, setCsvResultShortages] = useState<ShortageEntry[]>([]);
  const [repeatConfirmOrder, setRepeatConfirmOrder] = useState<Order | null>(null);
  /** Multi-select — empty = all statuses. Applied server-side, never against an already-fetched page. */
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>(() => savedStatusTabFilters.selectedStatuses ?? []);
  /** Time Period quick-filter — applied server-side via startDate/endDate, same as Order Inbox. */
  const [dateFilter, setDateFilter] = useState<TrackDateFilter>(() => savedStatusTabFilters.dateFilter ?? 'ALL');
  const [cancelTargetOrder, setCancelTargetOrder] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState<50 | 100>(50);
  const [pageNum, setPageNum] = useState(0);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [selectedDistributorId, setSelectedDistributorId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  /** Sidebar free-text search box — typed value updates instantly; the debounced value below is
   *  what's actually sent to the backend, so fast typing doesn't fire a request per keystroke. */
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  const [selectedReportId, setSelectedReportId] = useState<string>(TRACK_REPORT_DEFS[0].id);
  const [generateAnchor, setGenerateAnchor] = useState<null | HTMLElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      // For reps, the backend uses the tenant filter to return orders THEY placed.
      // For retailers, the backend uses a cross-tenant query by retailerTenantId
      // (which also surfaces rep-placed orders on their behalf).
      // Status / Time Period / search / manual date range / quick filters (Rep Orders, Schemes,
      // Shortages) / free-text search are ALL applied server-side here — never re-filtered against
      // an already-fetched page. Search and date range are lifted to MyOrdersPage (rendered beside
      // the Date/Supplier/Product Wise tabs), so they arrive here as props.
      //
      // Distributor/product drill-down is intentionally NOT sent to the backend here — the
      // sidebar's own supplier/product lists are built from this same `orders` array, and sending
      // the drill-down as a server-side filter would shrink `orders` down to just the selected
      // supplier/product, making every OTHER sidebar entry disappear. Drill-down stays a
      // client-side filter (in filteredOrders) over this already-fetched, broader batch.
      const quick = dateFilter !== 'ALL' ? computeTrackQuickDateRange(dateFilter) : {};
      const partyName = appliedTrackSearch?.mode === 'supplier' ? appliedTrackSearch.query : undefined;
      const partyPhone = appliedTrackSearch?.mode === 'phone' ? appliedTrackSearch.query : undefined;
      const orderNumber = appliedTrackSearch?.mode === 'orderId' ? appliedTrackSearch.query : undefined;
      const productQuery = appliedTrackSearch?.mode === 'product' ? appliedTrackSearch.query : undefined;
      const res = await ordersApi.list({
        size: 500,
        statuses: selectedStatuses.length ? selectedStatuses : undefined,
        startDate: quick.startDate ?? appliedDateRange?.startDate,
        endDate: quick.endDate ?? appliedDateRange?.endDate,
        partyName,
        partyPhone,
        orderNumber,
        productQuery,
        schemesOnly: quickFilter === 'schemes' ? true : undefined,
        hasShortages: quickFilter === 'shortages' ? true : undefined,
        anyRep: quickFilter === 'rep' ? true : undefined,
        q: debouncedSearch || undefined,
      });
      const content = res.content ?? [];
      setOrders(content);
      const rows = await ordersApi.getShortagesForOrders(content.map(o => o.id));
      setShortageStore(groupShortagesByOrder(rows));
    } catch {
      setError('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [user, selectedStatuses, dateFilter, appliedTrackSearch, appliedDateRange, quickFilter, debouncedSearch]);

  // Status, Time Period, search, date range, quick filters and free-text search are
  // all applied to the backend immediately on change — this also performs the initial load.
  useEffect(() => { void fetchOrders(); }, [fetchOrders]);

  // Persist filter selections in sessionStorage so navigating away and back restores them.
  useEffect(() => {
    saveSessionFilters('myOrdersStatusTab', { filterMode, quickFilter, viewMode, selectedStatuses, dateFilter });
  }, [filterMode, quickFilter, viewMode, selectedStatuses, dateFilter]);

  const clearTrackFilters = () => {
    setSelectedStatuses([]);
    setDateFilter('ALL');
    setQuickFilter('all');
    clearTrackSearch();
    clearManualDateRange();
  };

  /** Order count per status, shown next to each Status filter option. */
  const trackStatusCounts = useMemo(() => {
    const map = new Map<OrderStatus, number>();
    for (const o of orders) map.set(o.status, (map.get(o.status) ?? 0) + 1);
    return map;
  }, [orders]);

  /** Order count per Time Period quick-filter, shown next to each option ("All Time" excluded). */
  const trackDateFilterCounts = useMemo(() => {
    const map = new Map<TrackDateFilter, number>();
    map.set('ALL', orders.length);
    for (const v of ['TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH'] as TrackDateFilter[]) {
      const { startDate, endDate } = computeTrackQuickDateRange(v);
      map.set(v, orders.filter(o => {
        const d = toLocalTrackDateStr(new Date(o.orderDate ?? o.createdAt));
        return (!startDate || d >= startDate) && (!endDate || d <= endDate);
      }).length);
    }
    return map;
  }, [orders]);

  async function handleCancelOrder() {
    if (!cancelTargetOrder) return;
    setCancelling(true);
    try {
      await ordersApi.cancel(cancelTargetOrder.id, cancelReason || undefined);
      setCancelTargetOrder(null);
      setCancelReason('');
      void fetchOrders();
    } catch {
      // stay open on error
    } finally {
      setCancelling(false);
    }
  }

  async function handleDownloadInvoice(order: Order) {
    try {
      const blob = await ordersApi.downloadInvoice(order.id);
      triggerTrackDownload(blob, order.invoiceFileName || `invoice-${order.orderNumber}.pdf`);
    } catch {
      // best-effort — silently ignore, button stays clickable for retry
    }
  }

  useEffect(() => {
    tenantApi.listPeers(undefined, 0, 500).then(res => {
      const nameMap = new Map<number, string>();
      const infoMap = new Map<number, PeerTenant>();
      for (const p of res.content) {
        nameMap.set(p.id, p.name);
        infoMap.set(p.id, p);
      }
      setDistributorNames(nameMap);
      setDistributorInfo(infoMap);
    }).catch(() => {});
    // For reps: also load retailer names so we can display "For: [retailer]" in order rows.
    if (isRep) {
      tenantApi.listPeers(undefined, 0, 500, 'RETAILER').then(res => {
        const map = new Map<number, string>();
        for (const p of res.content) map.set(p.id, p.name);
        setRetailerNames(map);
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync view mode + sidebar when nav selection changes
  useEffect(() => {
    setViewMode(initialView);
    setPageNum(0);
    if (initialView === 'supplier') { setFilterMode('distributor'); setSelectedDistributorId(null); setSelectedProduct(null); }
    if (initialView === 'product')  { setFilterMode('product');     setSelectedDistributorId(null); setSelectedProduct(null); }
  }, [initialView]);

  // Handle CSV invoice upload for shortage comparison — persisted server-side so the filter and
  // badges are correct regardless of which device/browser recorded the shortage.
  function handleCsvUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !csvTargetOrder) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const text = ev.target?.result as string;
      const shortages = parseInvoiceCsv(text, csvTargetOrder.items ?? []);
      try {
        await ordersApi.recordShortages(csvTargetOrder.id, shortages);
        setShortageStore(prev => {
          const next = new Map(prev);
          next.set(csvTargetOrder.id, { orderId: csvTargetOrder.id, shortages });
          return next;
        });
        setCsvResultShortages(shortages);
        setCsvResultDlg(true);
      } catch {
        setError('Failed to save shortages for this order.');
      } finally {
        setCsvTargetOrder(null);
        setCheckItemsDlg(false);
      }
    };
    reader.readAsText(file);
  }

  // --- sidebar data ---

  const distributorEntries = useMemo(() => {
    const map = new Map<number, number>(); // id -> order count
    for (const o of orders) map.set(o.distributorTenantId, (map.get(o.distributorTenantId) ?? 0) + 1);
    return Array.from(map.entries()).sort((a, b) => {
      const na = distributorNames.get(a[0]) ?? '';
      const nb = distributorNames.get(b[0]) ?? '';
      return na.localeCompare(nb);
    });
  }, [orders, distributorNames]);

  const productEntries = useMemo(() => {
    const map = new Map<string, number>(); // productName -> order count (orders containing it)
    for (const o of orders) {
      const seen = new Set<string>();
      for (const item of o.items ?? []) {
        if (!seen.has(item.productName)) {
          map.set(item.productName, (map.get(item.productName) ?? 0) + 1);
          seen.add(item.productName);
        }
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [orders]);

  // --- filtered orders ---

  // Status, Time Period, search, and the Rep Orders/Schemes/Shortages quick filters are all
  // applied server-side in fetchOrders — never re-filtered here. Only the sidebar's
  // distributor/product drill-down stays client-side (see the comment in fetchOrders above).
  const filteredOrders = useMemo(() => {
    let base = orders;
    if (filterMode === 'distributor' && selectedDistributorId !== null) {
      base = base.filter(o => o.distributorTenantId === selectedDistributorId);
    }
    if (filterMode === 'product' && selectedProduct !== null) {
      base = base.filter(o => o.items?.some(i => i.productName === selectedProduct));
    }
    return base.slice().sort((a, b) => {
      const ta = new Date(a.orderDate ?? a.createdAt).getTime();
      const tb = new Date(b.orderDate ?? b.createdAt).getTime();
      return tb - ta;
    });
  }, [orders, filterMode, selectedDistributorId, selectedProduct]);

  useEffect(() => { setPageNum(0); }, [rowsPerPage, filteredOrders.length]);

  // Paginated orders + date groupings
  const pagedOrders = useMemo(() =>
    filteredOrders.slice(pageNum * rowsPerPage, (pageNum + 1) * rowsPerPage),
  [filteredOrders, pageNum, rowsPerPage]);

  const pagedByDate = useMemo(() => {
    const map = new Map<string, Order[]>();
    for (const o of pagedOrders) {
      const key = (o.orderDate ?? o.createdAt).slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [pagedOrders]);

  // Group by date → distributor (for "By Supplier" view) — paginated
  const pagedBySupplier = useMemo(() => {
    const byDate = new Map<string, Map<number, Order[]>>();
    for (const o of pagedOrders) {
      const dateKey = (o.orderDate ?? o.createdAt).slice(0, 10);
      if (!byDate.has(dateKey)) byDate.set(dateKey, new Map());
      const dm = byDate.get(dateKey)!;
      if (!dm.has(o.distributorTenantId)) dm.set(o.distributorTenantId, []);
      dm.get(o.distributorTenantId)!.push(o);
    }
    return Array.from(byDate.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dateKey, distMap]) => {
        const allOrders = pagedOrders.filter(o => (o.orderDate ?? o.createdAt).slice(0, 10) === dateKey);
        const dayTotal = allOrders.reduce((s, o) => s + (Number(o.totalAmount) - Number(o.totalTax)), 0);
        const dayItems = allOrders.reduce((s, o) => s + (o.items?.length ?? 0), 0);
        return {
          dateKey,
          allOrders,
          dayTotal,
          dayItems,
          distributors: Array.from(distMap.entries())
            .map(([distId, distOrders]) => ({ distId, distOrders }))
            .sort((a, b) => (distributorNames.get(a.distId) ?? '').localeCompare(distributorNames.get(b.distId) ?? '')),
        };
      });
  }, [pagedOrders, distributorNames]);

  // Group by date → product (for "By Product" view)
  const groupedByProductDate = useMemo(() => {
    type PEntry = {
      name: string; sku: string;
      orderSet: Set<number>; orderList: Order[];
      totalQty: number; totalFree: number; totalAmount: number;
      mrp: number; unitPrice: number; discountPct: number;
      distIds: Set<number>;
    };
    const byDate = new Map<string, Map<string, PEntry>>();
    for (const o of filteredOrders) {
      const dateKey = (o.orderDate ?? o.createdAt).slice(0, 10);
      if (!byDate.has(dateKey)) byDate.set(dateKey, new Map());
      const dateMap = byDate.get(dateKey)!;
      for (const item of o.items ?? []) {
        if (selectedProduct && item.productName !== selectedProduct) continue;
        const key = item.productName;
        if (!dateMap.has(key)) {
          dateMap.set(key, { name: key, sku: item.sku ?? '', orderSet: new Set(), orderList: [],
            totalQty: 0, totalFree: 0, totalAmount: 0,
            mrp: Number(item.mrp ?? 0), unitPrice: Number(item.unitPrice ?? 0),
            discountPct: Number(item.discountPct ?? 0), distIds: new Set() });
        }
        const e = dateMap.get(key)!;
        e.totalQty  += Number(item.qty);
        e.totalFree += Number(item.freeQty ?? 0);
        e.totalAmount += Number(item.lineTotal ?? 0) - Number(item.gstAmt ?? 0);
        e.distIds.add(o.distributorTenantId);
        if (!e.orderSet.has(o.id)) { e.orderSet.add(o.id); e.orderList.push(o); }
      }
    }
    return Array.from(byDate.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dateKey, productMap]) => {
        const products = Array.from(productMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        const allOrderIds = new Set(products.flatMap(p => Array.from(p.orderSet)));
        const totalItems = products.reduce((s, p) => s + p.totalQty, 0);
        const dateTotal  = products.reduce((s, p) => s + p.totalAmount, 0);
        return { dateKey, products, orderCount: allOrderIds.size, totalItems, dateTotal };
      });
  }, [filteredOrders]);

  // Flat product rows for pagination
  const allProductRows = useMemo(() => {
    const rows: Array<{ dateKey: string; prod: typeof groupedByProductDate[number]['products'][number] }> = [];
    for (const { dateKey, products } of groupedByProductDate) {
      for (const prod of products) rows.push({ dateKey, prod });
    }
    return rows;
  }, [groupedByProductDate]);

  const pagedProductRows = useMemo(() =>
    allProductRows.slice(pageNum * rowsPerPage, (pageNum + 1) * rowsPerPage),
  [allProductRows, pageNum, rowsPerPage]);


  function selectDistributor(id: number) {
    setFilterMode('distributor');
    setSelectedDistributorId(prev => (prev === id ? null : id));
    setSelectedProduct(null);
  }

  function selectProduct(name: string) {
    setFilterMode('product');
    setSelectedProduct(prev => (prev === name ? null : name));
    setSelectedDistributorId(null);
  }

  // ── Reports data ────────────────────────────────────────────────────────
  const todayStr = new Date().toDateString();
  const todayOrders = orders.filter(o => new Date(o.orderDate ?? o.createdAt).toDateString() === todayStr);

  const todayBySupplier = useMemo(() => {
    const map = new Map<number, number>();
    for (const o of todayOrders) map.set(o.distributorTenantId, (map.get(o.distributorTenantId) ?? 0) + 1);
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const schemeOrderCount = useMemo(
    () => orders.filter(o => o.items?.some(i => Number(i.freeQty) > 0)).length,
    [orders],
  );

  const shortageOrderCount = useMemo(
    () => orders.filter(o => (shortageStore.get(o.id)?.shortages.length ?? 0) > 0).length,
    [orders, shortageStore],
  );

  const shortagesBySupplier = useMemo(() => {
    const map = new Map<number, number>();
    shortageStore.forEach(rec => {
      if (rec.shortages.length > 0) {
        const order = orders.find(o => o.id === rec.orderId);
        if (order) map.set(order.distributorTenantId, (map.get(order.distributorTenantId) ?? 0) + 1);
      }
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [orders, shortageStore]);

  /** All my orders grouped by status — Order Status Summary report. */
  const reportOrderStatus = useMemo(() => {
    const map = new Map<string, { label: string; count: number; revenue: number }>();
    for (const o of orders) {
      const key = o.status;
      const label = ORDER_STATUS_MAP[o.status]?.label ?? o.status;
      const ex = map.get(key) ?? { label, count: 0, revenue: 0 };
      ex.count++;
      ex.revenue += (Number(o.totalAmount) - Number(o.totalTax)) || 0;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([status, v]) => ({ status, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [orders]);

  /** Orders grouped by distributor / supplier — Supplier-wise Summary report. */
  const reportSupplierWise = useMemo(() => {
    const map = new Map<number, { name: string; count: number; revenue: number; lastDate: string }>();
    for (const o of orders) {
      const key = o.distributorTenantId;
      const name = distributorNames.get(key) ?? `Supplier #${key}`;
      const date = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
      const ex = map.get(key) ?? { name, count: 0, revenue: 0, lastDate: date };
      ex.count++;
      ex.revenue += (Number(o.totalAmount) - Number(o.totalTax)) || 0;
      if (date > ex.lastDate) ex.lastDate = date;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ supplierId: id, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [orders, distributorNames]);

  /** Orders grouped by date — Daily Orders report. */
  const reportDailyOrders = useMemo(() => {
    const map = new Map<string, { count: number; items: number; revenue: number }>();
    for (const o of orders) {
      const key = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
      const ex = map.get(key) ?? { count: 0, items: 0, revenue: 0 };
      ex.count++;
      ex.items += o.items?.reduce((s, i) => s + (i.qty || 0), 0) ?? 0;
      ex.revenue += (Number(o.totalAmount) - Number(o.totalTax)) || 0;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([date, v]) => ({ date, ...v }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [orders]);

  function downloadTrackReportExcel() {
    const def = TRACK_REPORT_DEFS.find(r => r.id === selectedReportId)!;
    let headers: string[];
    let rows: (string | number)[][];

    if (selectedReportId === 'supplier-wise') {
      headers = ['Supplier', 'Orders', 'Revenue (₹)', 'Last Order Date'];
      rows = reportSupplierWise.map(s => [s.name, s.count, s.revenue.toFixed(2), s.lastDate]);
      rows.push(['TOTAL', reportSupplierWise.reduce((a,s)=>a+s.count,0), reportSupplierWise.reduce((a,s)=>a+s.revenue,0).toFixed(2), '']);
    } else if (selectedReportId === 'daily-orders') {
      headers = ['Date', 'Orders', 'Total Items', 'Revenue (₹)'];
      rows = reportDailyOrders.map(d => [d.date, d.count, d.items, d.revenue.toFixed(2)]);
      rows.push(['TOTAL', reportDailyOrders.reduce((a,d)=>a+d.count,0), reportDailyOrders.reduce((a,d)=>a+d.items,0), reportDailyOrders.reduce((a,d)=>a+d.revenue,0).toFixed(2)]);
    } else {
      // order-status
      const totalRev = reportOrderStatus.reduce((a,s)=>a+s.revenue,0);
      headers = ['Status', 'Orders', 'Revenue (₹)', 'Revenue Share (%)'];
      rows = reportOrderStatus.map(s => [s.label, s.count, s.revenue.toFixed(2), totalRev>0?((s.revenue/totalRev)*100).toFixed(1)+'%':'0%']);
      rows.push(['TOTAL', reportOrderStatus.reduce((a,s)=>a+s.count,0), reportOrderStatus.reduce((a,s)=>a+s.revenue,0).toFixed(2), '100%']);
    }

    const content = [headers, ...rows].map(r => r.join('\t')).join('\n');
    triggerTrackDownload(new Blob(['﻿' + content], { type: 'application/vnd.ms-excel' }), `${def.id}_report.xls`);
  }

  function downloadTrackReportPdf() {
    const def = TRACK_REPORT_DEFS.find(r => r.id === selectedReportId)!;
    const style = `body{font-family:sans-serif;font-size:12px;margin:24px}h3{margin:0 0 2px}p{margin:0 0 14px;color:#6b7280;font-size:11px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #e5e7eb;padding:5px 9px}th{background:#f3f4f6;font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.4px}tr:nth-child(even){background:#fafafa}.total-row{background:#f3f4f6;font-weight:700}`;
    let tableHtml = '';

    if (selectedReportId === 'supplier-wise') {
      const totalRev = reportSupplierWise.reduce((a,s)=>a+s.revenue,0);
      const totalCnt = reportSupplierWise.reduce((a,s)=>a+s.count,0);
      tableHtml = `<table><thead><tr>${['Supplier','Orders','Revenue (₹)','Last Order'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        reportSupplierWise.map(s=>`<tr><td><b>${s.name}</b></td><td style="text-align:right">${s.count}</td><td style="text-align:right">₹${formatTrackAmount(s.revenue)}</td><td>${s.lastDate}</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL — ${reportSupplierWise.length} suppliers</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">₹${formatTrackAmount(totalRev)}</td><td></td></tr></tfoot></table>`;
    } else if (selectedReportId === 'daily-orders') {
      const totalCnt = reportDailyOrders.reduce((a,d)=>a+d.count,0);
      const totalItems = reportDailyOrders.reduce((a,d)=>a+d.items,0);
      const totalRev = reportDailyOrders.reduce((a,d)=>a+d.revenue,0);
      tableHtml = `<table><thead><tr>${['Date','Orders','Total Items','Revenue (₹)'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        reportDailyOrders.map(d=>`<tr><td><b>${d.date}</b></td><td style="text-align:right">${d.count}</td><td style="text-align:right">${d.items}</td><td style="text-align:right">₹${formatTrackAmount(d.revenue)}</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">${totalItems}</td><td style="text-align:right">₹${formatTrackAmount(totalRev)}</td></tr></tfoot></table>`;
    } else {
      const totalRev = reportOrderStatus.reduce((a,s)=>a+s.revenue,0);
      const totalCnt = reportOrderStatus.reduce((a,s)=>a+s.count,0);
      tableHtml = `<table><thead><tr>${['Status','Orders','Revenue (₹)','Revenue Share (%)'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        reportOrderStatus.map(s=>`<tr><td><b>${s.label}</b></td><td style="text-align:right">${s.count}</td><td style="text-align:right">₹${formatTrackAmount(s.revenue)}</td><td style="text-align:right">${totalRev>0?((s.revenue/totalRev)*100).toFixed(1):0}%</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">₹${formatTrackAmount(totalRev)}</td><td style="text-align:right">100%</td></tr></tfoot></table>`;
    }

    const html = `<!DOCTYPE html><html><head><title>${def.name}</title><style>${style}</style></head><body><h3>${def.name}</h3><p>${def.description} &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</p>${tableHtml}</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  }

  const sidebarItemSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    cursor: 'pointer',
    bgcolor: active ? '#eff6ff' : 'transparent',
    borderLeft: active ? '3px solid #1a56db' : '3px solid transparent',
    '&:hover': { bgcolor: active ? '#eff6ff' : '#f5f5f5' },
  });

  const filteredDistributorEntries = useMemo((): [number, number][] => {
    const q = search.trim().toLowerCase();
    return q
      ? distributorEntries.filter(([id]) => (distributorNames.get(id) ?? '').toLowerCase().includes(q))
      : distributorEntries;
  }, [search, distributorEntries, distributorNames]);

  const filteredProductEntries = useMemo((): [string, number][] => {
    const q = search.trim().toLowerCase();
    return q ? productEntries.filter(([name]) => name.toLowerCase().includes(q)) : productEntries;
  }, [search, productEntries]);

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left sidebar ── */}
      <Box sx={{ width: 210, flexShrink: 0, borderRight: '1px solid #e5e7eb', bgcolor: '#fafbfc', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.5 }}>
            Filter By
          </Typography>
          <Tooltip title="Refresh orders">
            <span>
              <IconButton size="small" onClick={fetchOrders} disabled={loading} sx={{ p: 0.3 }}>
                {loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Search */}
        <Box sx={{ px: 1.25, py: 0.75, borderBottom: '1px solid #e5e7eb' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: '#9ca3af' }} /></InputAdornment>,
              sx: { fontSize: 12, '& input': { py: '5px' } },
            }}
          />
        </Box>

        {/* Mode tabs */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
          {([
            { id: 'distributor', label: 'Supplier', Icon: StoreIcon },
            { id: 'product',     label: 'Product',  Icon: CategoryIcon },
            { id: 'reports',     label: 'Reports',  Icon: ReportsIcon },
          ] as { id: TrackFilterMode; label: string; Icon: React.ElementType }[]).map(({ id, label, Icon }) => (
            <Box
              key={id}
              onClick={() => { setFilterMode(id); setSelectedDistributorId(null); setSelectedProduct(null); }}
              sx={{
                flex: 1,
                py: 0.75,
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: filterMode === id ? 700 : 500,
                color: filterMode === id ? '#1a56db' : '#6b7280',
                borderBottom: filterMode === id ? '2px solid #1a56db' : '2px solid transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.3,
              }}
            >
              <Icon sx={{ fontSize: 12 }} />
              {label}
            </Box>
          ))}
        </Box>

        {/* Sidebar list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {filterMode === 'distributor' && (
            <>
              {/* "All" row */}
              <Box
                sx={sidebarItemSx(selectedDistributorId === null)}
                onClick={() => setSelectedDistributorId(null)}
              >
                <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#374151' }}>All Suppliers</Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{orders.length}</Typography>
              </Box>
              {filteredDistributorEntries.map(([id, count]) => (
                <Box key={id} sx={sidebarItemSx(selectedDistributorId === id)} onClick={() => selectDistributor(id)}>
                  <Typography sx={{ flex: 1, fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {distributorNames.get(id) ?? `Supplier #${id}`}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{count}</Typography>
                </Box>
              ))}
              {distributorEntries.length === 0 && !loading && (
                <Typography sx={{ fontSize: 12, color: '#9ca3af', p: 1.5 }}>No orders yet.</Typography>
              )}
            </>
          )}

          {filterMode === 'product' && (
            <>
              {/* "All" row */}
              <Box
                sx={sidebarItemSx(selectedProduct === null)}
                onClick={() => setSelectedProduct(null)}
              >
                <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#374151' }}>All Products</Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{productEntries.reduce((s, [, c]) => s + c, 0)}</Typography>
              </Box>
              {filteredProductEntries.map(([name, count]) => (
                <Box key={name} sx={sidebarItemSx(selectedProduct === name)} onClick={() => selectProduct(name)}>
                  <Typography sx={{ flex: 1, fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{count}</Typography>
                </Box>
              ))}
              {productEntries.length === 0 && !loading && (
                <Typography sx={{ fontSize: 12, color: '#9ca3af', p: 1.5 }}>No products in orders.</Typography>
              )}
            </>
          )}

          {filterMode === 'reports' && (
            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Today's orders by supplier */}
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                  Today by Supplier
                </Typography>
                {todayBySupplier.length === 0
                  ? <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>No orders today.</Typography>
                  : todayBySupplier.map(([id, cnt]) => (
                      <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, cursor: 'pointer', borderRadius: 0.5, px: 0.5, '&:hover': { bgcolor: '#f0f7ff' } }}
                        onClick={() => { setFilterMode('distributor'); setSelectedDistributorId(id); setDateFilter('TODAY'); }}>
                        <Typography sx={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {distributorNames.get(id) ?? `#${id}`}
                        </Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a56db', flexShrink: 0, ml: 0.5 }}>{cnt}</Typography>
                      </Box>
                    ))}
              </Box>

              {/* Quick-filter stats */}
              <Box sx={{ borderTop: '1px solid #f3f4f6', pt: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                  Quick Views
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 0.5, bgcolor: dateFilter === 'TODAY' ? '#eff6ff' : 'transparent', '&:hover': { bgcolor: '#f0f7ff' } }}
                  onClick={() => { setDateFilter(d => d === 'TODAY' ? 'ALL' : 'TODAY'); setFilterMode('distributor'); setSelectedDistributorId(null); }}>
                  <Typography sx={{ fontSize: 11, color: '#374151' }}>Today&apos;s Orders</Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a56db' }}>{todayOrders.length}</Typography>
                </Box>
                {[
                  { label: 'Scheme Items',    count: schemeOrderCount,   filter: 'schemes' as QuickFilter, color: '#7c3aed' },
                  { label: 'Shortage Orders', count: shortageOrderCount, filter: 'shortages' as QuickFilter, color: '#dc2626' },
                ].map(r => (
                  <Box key={r.filter} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 0.5, bgcolor: quickFilter === r.filter ? '#eff6ff' : 'transparent', '&:hover': { bgcolor: '#f0f7ff' } }}
                    onClick={() => { setQuickFilter(q => q === r.filter ? 'all' : r.filter); setFilterMode('distributor'); setSelectedDistributorId(null); }}>
                    <Typography sx={{ fontSize: 11, color: '#374151' }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: r.color }}>{r.count}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Most shortages by supplier */}
              {shortagesBySupplier.length > 0 && (
                <Box sx={{ borderTop: '1px solid #f3f4f6', pt: 1 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                    Most Shortages
                  </Typography>
                  {shortagesBySupplier.slice(0, 5).map(([id, cnt]) => (
                    <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, px: 0.5, cursor: 'pointer', borderRadius: 0.5, '&:hover': { bgcolor: '#fff5f5' } }}
                      onClick={() => { setFilterMode('distributor'); setSelectedDistributorId(id); setQuickFilter('shortages'); }}>
                      <Typography sx={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {distributorNames.get(id) ?? `#${id}`}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626', flexShrink: 0, ml: 0.5 }}>{cnt} bill{cnt !== 1 ? 's' : ''}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ── Reports Panel (replaces order list when filterMode==='reports') ── */}
        {filterMode === 'reports' && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Single header strip: title + stats | report selector + Generate — all on one line */}
            <Box sx={{ px: 2, py: 0.6, borderBottom: '1px solid #e5e7eb', bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'wrap' }}>
              <ReportsIcon sx={{ color: '#1a56db', fontSize: 15, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', whiteSpace: 'nowrap' }}>Reports</Typography>
              <Typography sx={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {orders.length} order{orders.length !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {/* Searchable report selector */}
              <Autocomplete
                size="small"
                options={TRACK_REPORT_DEFS}
                getOptionLabel={(o) => o.name}
                value={TRACK_REPORT_DEFS.find(r => r.id === selectedReportId) ?? TRACK_REPORT_DEFS[0]}
                onChange={(_, v) => { if (v) setSelectedReportId(v.id); }}
                disableClearable
                sx={{ width: 240 }}
                slotProps={{ popper: { sx: { zIndex: 1400 } } }}
                ListboxProps={{ sx: { maxHeight: 260, p: 0 } }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select report…"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, bgcolor: '#fff' } }} />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: '7px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{option.name}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{option.description}</div>
                  </li>
                )}
              />
              {/* Generate button */}
              <Button size="small" variant="contained"
                endIcon={<ArrowDropDownIcon sx={{ fontSize: '16px !important', ml: -0.5 }} />}
                onClick={(e) => setGenerateAnchor(e.currentTarget)}
                sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#1a56db', '&:hover': { bgcolor: '#1e40af' }, whiteSpace: 'nowrap', textTransform: 'none', px: 1.5, py: '3px', minWidth: 0, flexShrink: 0 }}>
                Generate
              </Button>
              <Menu anchorEl={generateAnchor} open={Boolean(generateAnchor)} onClose={() => setGenerateAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 190, mt: 0.5 } }}>
                <MenuItem dense sx={{ fontSize: 12, gap: 1, py: 0.8 }} onClick={() => { downloadTrackReportExcel(); setGenerateAnchor(null); }}>
                  <XlsIcon sx={{ fontSize: 15, color: '#15803d' }} /> Download Excel (.xls)
                </MenuItem>
                <MenuItem dense sx={{ fontSize: 12, gap: 1, py: 0.8 }} onClick={() => { downloadTrackReportPdf(); setGenerateAnchor(null); }}>
                  <PrintIcon sx={{ fontSize: 15, color: '#dc2626' }} /> Download / Print PDF
                </MenuItem>
              </Menu>
            </Box>

            {orders.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
                <ReportsIcon sx={{ fontSize: 36, opacity: 0.25 }} />
                <Typography variant="body2">No orders found to generate reports.</Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'auto' }}>

                {/* ── Order Status Summary ── */}
                {selectedReportId === 'order-status' && (() => {
                  const totalRev = reportOrderStatus.reduce((a,s)=>a+s.revenue,0);
                  const totalCnt = reportOrderStatus.reduce((a,s)=>a+s.count,0);
                  return (
                    <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Status','Orders','Revenue (₹)','Revenue Share %'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportOrderStatus.map((s, idx) => {
                          const bg = ORDER_STATUS_MAP[s.status as OrderStatus]?.bg ?? '#9ca3af';
                          return (
                            <tr key={s.status} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '5px 12px' }}>
                                <span style={{ padding: '2px 7px', borderRadius: 4, background: bg, color: '#fff', fontSize: 10, fontWeight: 700 }}>{s.label.toUpperCase()}</span>
                              </td>
                              <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{s.count}</td>
                              <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(s.revenue)}</td>
                              <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                                <span style={{ padding: '2px 7px', borderRadius: 4, background: '#f0fdf4', color: '#16a34a', fontWeight: 700 }}>{totalRev>0?((s.revenue/totalRev)*100).toFixed(1):0}%</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(totalRev)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {/* ── Supplier-wise Summary ── */}
                {selectedReportId === 'supplier-wise' && (() => {
                  const totalRev = reportSupplierWise.reduce((a,s)=>a+s.revenue,0);
                  const totalCnt = reportSupplierWise.reduce((a,s)=>a+s.count,0);
                  return (
                    <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Supplier','Orders','Revenue (₹)','Last Order'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0||i===3?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportSupplierWise.map((s, idx) => (
                          <tr key={s.supplierId} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                            onClick={() => { setFilterMode('distributor'); setSelectedDistributorId(s.supplierId); }}>
                            <td style={{ padding: '5px 12px', fontWeight: 600, color: '#1a56db' }}>{s.name}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{s.count}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(s.revenue)}</td>
                            <td style={{ padding: '5px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{s.lastDate}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total — {reportSupplierWise.length} suppliers</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(totalRev)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {/* ── Daily Orders Report ── */}
                {selectedReportId === 'daily-orders' && (() => {
                  const totalCnt   = reportDailyOrders.reduce((a,d)=>a+d.count,0);
                  const totalItems = reportDailyOrders.reduce((a,d)=>a+d.items,0);
                  const totalRev   = reportDailyOrders.reduce((a,d)=>a+d.revenue,0);
                  return (
                    <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Date','Orders','Total Items','Revenue (₹)'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {reportDailyOrders.map((d, idx) => (
                          <tr key={d.date} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{d.date}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{d.count}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', color: '#374151' }}>{d.items}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(d.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total — {reportDailyOrders.length} days</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>{totalItems}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatTrackAmount(totalRev)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

              </Box>
            )}
          </Box>
        )}

        {/* Top bar — shown only when NOT in reports mode */}
        {filterMode !== 'reports' && (
        <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid #e5e7eb', bgcolor: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton size="small" onClick={() => setSidebarOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, p: 0.5, color: (selectedDistributorId !== null || selectedProduct !== null || quickFilter !== 'all') ? '#7B61FF' : '#6b7280' }}>
            <FilterListIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', flex: 1, minWidth: 120 }}>
            {filterMode === 'distributor' && selectedDistributorId
              ? `Orders → ${distributorNames.get(selectedDistributorId) ?? `Supplier #${selectedDistributorId}`}`
              : filterMode === 'product' && selectedProduct
              ? `Orders containing: ${selectedProduct}`
              : 'All My Orders'}
          </Typography>

          {/* Time Period — applied server-side, same as Order Inbox */}
          <FormControl size="small" sx={{ minWidth: 110, flexShrink: 0 }}>
            <Select
              value={dateFilter}
              onChange={e => { setDateFilter(e.target.value as TrackDateFilter); setPageNum(0); }}
              sx={{ fontSize: 11, height: 26, '.MuiSelect-select': { py: '2px !important', pr: '28px !important' } }}
            >
              {(['ALL', 'TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH'] as TrackDateFilter[]).map(v => (
                <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
                  {v === 'ALL' ? 'All Time' : v === 'TODAY' ? 'Today' : v === 'YESTERDAY' ? 'Yesterday' : v === 'THIS_WEEK' ? 'This Week' : 'This Month'}
                  {/* Count hidden for "All Time" — keeping the code, just not rendering it.
                  <Box component="span" sx={{ ml: 0.5, fontWeight: 700, color: '#7c3aed' }}>({trackDateFilterCounts.get(v) ?? 0})</Box>
                  */}
                  {v !== 'ALL' && (
                    <Box component="span" sx={{ ml: 0.5, fontWeight: 700, color: '#7c3aed' }}>({trackDateFilterCounts.get(v) ?? 0})</Box>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status — multi-select, applied server-side */}
          <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
            <Select
              multiple
              displayEmpty
              value={selectedStatuses}
              onChange={e => { setSelectedStatuses(e.target.value as OrderStatus[]); setPageNum(0); }}
              renderValue={(vals) => (vals as OrderStatus[]).length === 0 ? 'All Status' : `${(vals as OrderStatus[]).length} status${(vals as OrderStatus[]).length > 1 ? 'es' : ''}`}
              sx={{ fontSize: 11, height: 26, '.MuiSelect-select': { py: '2px !important', pr: '28px !important' } }}
            >
              {MY_ORDERS_FILTER_STATUSES.map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12, py: 0.25 }}>
                  <Checkbox size="small" checked={selectedStatuses.includes(s)} sx={{ p: 0.3 }} />
                  <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: ORDER_STATUS_MAP[s].bg, mr: 0.75 }} />
                  <ListItemText primary={ORDER_STATUS_MAP[s].label} primaryTypographyProps={{ fontSize: 12 }} />
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: ORDER_STATUS_MAP[s].bg }}>({trackStatusCounts.get(s) ?? 0})</Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Quick-filter pills — Rep Orders is a simple client-side toggle, same as Schemes/Shortages.
              Hidden for Rep viewers: every order on this screen was already placed by them, so the
              toggle would always be 100% true and adds nothing. */}
          {(isRep ? (['schemes', 'shortages'] as QuickFilter[]) : (['rep', 'schemes', 'shortages'] as QuickFilter[])).map(f => {
            const labels: Record<QuickFilter, string> = { all: 'All', rep: 'Rep Orders', schemes: 'Schemes', shortages: 'Shortages' };
            const colors: Record<QuickFilter, string> = { all: '#374151', rep: '#1a56db', schemes: '#7c3aed', shortages: '#dc2626' };
            const active = quickFilter === f;
            return (
              <Box key={f} onClick={() => setQuickFilter(q => q === f ? 'all' : f)} sx={{
                px: 1, py: 0.2, borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                bgcolor: active ? colors[f] : '#f3f4f6',
                color: active ? '#fff' : colors[f],
                border: `1px solid ${active ? colors[f] : '#e5e7eb'}`,
                userSelect: 'none',
              }}>
                {labels[f]}
              </Box>
            );
          })}

          {(selectedStatuses.length > 0 || dateFilter !== 'ALL' || quickFilter !== 'all' || Boolean(appliedTrackSearch?.query.trim()) || appliedDateRange) && (
            <Tooltip title="Clear filters">
              <Box component="button" onClick={clearTrackFilters} sx={{
                border: '1px solid #ef4444', bgcolor: 'transparent', color: '#ef4444', fontSize: 10, fontWeight: 700,
                px: 0.75, py: 0.3, borderRadius: 0.5, cursor: 'pointer', letterSpacing: 0.3, flexShrink: 0,
                '&:hover': { bgcolor: '#fef2f2' },
              }}>
                × CLEAR
              </Box>
            </Tooltip>
          )}

          <Typography sx={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
            {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        )}

        {filterMode !== 'reports' && error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: 12 }}>{error}</Alert>}

        {filterMode !== 'reports' && loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}

        {filterMode !== 'reports' && !loading && filteredOrders.length === 0 && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#9ca3af' }}>No orders found.</Typography>
          </Box>
        )}

        {/* Cancel Order dialog */}
        <Dialog open={cancelTargetOrder !== null} onClose={() => { setCancelTargetOrder(null); setCancelReason(''); }} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Cancel Order</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: '#374151' }}>
              Cancel order <strong>#{cancelTargetOrder?.orderNumber || cancelTargetOrder?.id}</strong>? This cannot be undone.
            </Typography>
            <TextField
              fullWidth size="small" multiline rows={2}
              label="Reason (optional)"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button onClick={() => { setCancelTargetOrder(null); setCancelReason(''); }} size="small">No, Keep</Button>
            <Button
              variant="contained" size="small" disabled={cancelling}
              onClick={handleCancelOrder}
              sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
            >
              {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* CSV result dialog */}
        <Dialog open={csvResultDlg} onClose={() => setCsvResultDlg(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {csvResultShortages.length === 0 ? '✅ No Shortages Found' : `⚠ ${csvResultShortages.length} Item${csvResultShortages.length > 1 ? 's' : ''} Short`}
            <IconButton size="small" onClick={() => setCsvResultDlg(false)} sx={{ color: '#9ca3af' }}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {csvResultShortages.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
                  All items match the invoice. Nothing is short.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1.5 }}>
                  The following items were received in lesser quantity than ordered:
                </Typography>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ bgcolor: '#f9fafb' }}>
                      {['Product', 'Ordered', 'Received', 'Short'].map(h => (
                        <Box key={h} component="th" sx={{ px: 1.5, py: 0.75, textAlign: h === 'Product' ? 'left' : 'right', fontWeight: 700, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {csvResultShortages.map((s, i) => (
                      <Box key={i} component="tr" sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                        <Box component="td" sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', color: '#152346', fontWeight: 500 }}>{s.productName}</Box>
                        <Box component="td" sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: '#374151' }}>{s.orderedQty}</Box>
                        <Box component="td" sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', textAlign: 'right', color: '#374151' }}>{s.receivedQty}</Box>
                        <Box component="td" sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{s.shortageQty}</Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2 }}>
            <Button variant="contained" onClick={() => setCsvResultDlg(false)} sx={{ bgcolor: '#7B61FF', '&:hover': { bgcolor: '#6d54e8' }, fontSize: 13 }}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Check Items dialog */}
        <Dialog open={checkItemsDlg} onClose={() => { setCheckItemsDlg(false); setCsvTargetOrder(null); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1.5, borderBottom: '1px solid #e5e7eb', pr: 6 }}>
            Check Received Items Against Invoice
            <IconButton size="small" onClick={() => { setCheckItemsDlg(false); setCsvTargetOrder(null); }}
              sx={{ position: 'absolute', right: 10, top: 10 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Alert severity="info" icon={false}
              sx={{ mb: 2, fontSize: 12, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', '& .MuiAlert-message': { width: '100%' } }}>
              📋 <strong>Note:</strong> Upload your supplier's invoice CSV to automatically detect missing
              items and quantity shortages. This helps you verify what you actually received against what
              you ordered — including free scheme items.
            </Alert>
            {csvTargetOrder && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346' }}>
                  Order #{csvTargetOrder.orderNumber || csvTargetOrder.id}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.25 }}>
                  {csvTargetOrder.items?.length ?? 0} product{(csvTargetOrder.items?.length ?? 0) !== 1 ? 's' : ''}
                  &nbsp;·&nbsp; {distributorNames.get(csvTargetOrder.distributorTenantId) ?? 'Supplier'}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
              The CSV should contain columns for:<br />
              • <strong>Item Code</strong> or <strong>SKU</strong> — to match products<br />
              • <strong>Quantity</strong> or <strong>Received Qty</strong> — actual received quantity<br />
              Any item with received quantity less than ordered (including free items) will be flagged as a shortage.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button onClick={() => { setCheckItemsDlg(false); setCsvTargetOrder(null); }} size="small">Cancel</Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => { setCheckItemsDlg(false); csvInputRef.current?.click(); }}
              sx={{ bgcolor: '#152346', '&:hover': { bgcolor: '#1e3460' } }}
            >
              Upload Invoice CSV
            </Button>
          </DialogActions>
        </Dialog>

        {/* Hidden CSV file input for Check Items */}
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleCsvUpload}
        />

        {/* Repeat Order confirmation dialog */}
        <Dialog open={repeatConfirmOrder !== null} onClose={() => setRepeatConfirmOrder(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Repeat Order</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: '#374151' }}>
              Re-order the same <strong>{repeatConfirmOrder?.items?.length ?? 0} product{(repeatConfirmOrder?.items?.length ?? 0) !== 1 ? 's' : ''}</strong> from{' '}
              <strong>{distributorNames.get(repeatConfirmOrder?.distributorTenantId ?? 0) ?? `Supplier #${repeatConfirmOrder?.distributorTenantId}`}</strong>?
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6b7280', mt: 1 }}>
              Order #{repeatConfirmOrder?.orderNumber || repeatConfirmOrder?.id} &nbsp;·&nbsp; ₹{((Number(repeatConfirmOrder?.totalAmount) || 0) - (Number(repeatConfirmOrder?.totalTax) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.75 }}>
              All products and quantities will be pre-filled in the Place Order tab. You can adjust before placing.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button onClick={() => setRepeatConfirmOrder(null)} size="small">Cancel</Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                if (!repeatConfirmOrder) return;
                const distName = distributorNames.get(repeatConfirmOrder.distributorTenantId) ?? `Supplier #${repeatConfirmOrder.distributorTenantId}`;
                navigate('/orders', {
                  state: {
                    repeatOrder: {
                      orderNumber: repeatConfirmOrder.orderNumber ?? String(repeatConfirmOrder.id),
                      distributorTenantId: repeatConfirmOrder.distributorTenantId,
                      distributorName: distName,
                      items: (repeatConfirmOrder.items ?? []).map(i => ({
                        productId: i.productId,
                        productName: i.productName,
                        sku: i.sku,
                        uom: i.uom,
                        qty: i.qty,
                        freeQty: i.freeQty ?? 0,
                        ptr: i.unitPrice ?? i.ptr,
                        unitPrice: i.unitPrice,
                        mrp: i.mrp,
                      })),
                    },
                    bookOrderTab: 1,
                    ts: Date.now(),
                  },
                });
                setRepeatConfirmOrder(null);
              }}
              sx={{ bgcolor: '#097969', '&:hover': { bgcolor: '#075c50' } }}
            >
              Yes, Repeat
            </Button>
          </DialogActions>
        </Dialog>

        {/* Order list */}
        {filterMode !== 'reports' && !loading && filteredOrders.length > 0 && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ minWidth: 560 }}>
            {viewMode === 'date' ? (
              /* ── BY DATE view ── */
              pagedByDate.map(([groupKey, dayOrders]) => {
                const sample = dayOrders[0];
                const d = new Date(sample.orderDate ?? sample.createdAt);
                const now = new Date();
                const isToday = d.toDateString() === now.toDateString();
                const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
                const isYesterday = d.toDateString() === yesterday.toDateString();
                const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                const dayPart = isToday ? 'Today' : isYesterday ? 'Yesterday' : d.toLocaleDateString('en-GB', { weekday: 'long' });
                const dayTotal = dayOrders.reduce((s, o) => s + (Number(o.totalAmount) - Number(o.totalTax)), 0);
                const dayItems = dayOrders.reduce((s, o) => s + (o.items?.length ?? 0), 0);

                return (
                  <Box key={groupKey}>
                    {/* Date group header — date on the left, value/order count/item count on the right, same line, matching Order Inbox */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, px: 2, py: 0.6, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346', whiteSpace: 'nowrap' }}>
                        {datePart} <Box component="span" sx={{ color: '#008080' }}>[{dayPart}]</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#000', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {'₹'}<Box component="span" sx={{ fontWeight: 700, color: '#152346' }}>{formatTrackAmount(dayTotal)}</Box>{' · '}
                        <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayOrders.length}</Box>{` order${dayOrders.length !== 1 ? 's' : ''} · `}
                        <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayItems}</Box>{` item${dayItems !== 1 ? 's' : ''}`}
                      </Typography>
                    </Box>

                    {/* Order rows */}
                    {dayOrders.map(order => {
                      const distName = distributorNames.get(order.distributorTenantId) ?? `Supplier #${order.distributorTenantId}`;
                      const distPeer = distributorInfo.get(order.distributorTenantId);
                      const distLocation = [distPeer?.area, distPeer?.city].filter(Boolean).join(', ');
                      const shortage = shortageStore.get(order.id);
                      const hasShortage = (shortage?.shortages.length ?? 0) > 0;
                      const canCheckItems = order.status === 'DELIVERED';
                      const statusDef = ORDER_STATUS_MAP[order.status] ?? { label: order.status, bg: '#9ca3af' };
                      const itemCount = order.items?.length ?? 0;
                      const isRejected = order.status === 'REJECTED' || order.status === 'CANCELLED';
                      const matchedItems = selectedProduct
                        ? (order.items ?? []).filter(i => i.productName === selectedProduct)
                        : [];
                      // Rep orders: show "For: [retailer]" badge; Retailer orders: show "Via Rep" badge.
                      const forRetailerName = isRep
                        ? (retailerNames.get(order.retailerTenantId) ?? `Retailer #${order.retailerTenantId}`)
                        : null;
                      const viaRepName = !isRep ? (order.salesRepName ?? null) : null;
                      const expanded = expandedOrderIds.has(order.id);
                      const timeStr = formatTrackTime(order.orderDate ?? order.createdAt);

                      return (
                        <Box key={order.id}>
                        <Box onClick={() => navigate(`/orders/my/${order.id}`)}
                          sx={{ display: 'grid', gridTemplateColumns: MY_ORDERS_COLS, columnGap: 1, alignItems: 'center', borderBottom: expanded ? 'none' : '1px solid #e5e7eb', bgcolor: viaRepName ? '#faf5ff' : '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, minHeight: 44 }}>

                          {/* Col 1: expand chevron */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => { e.stopPropagation(); toggleOrderExpanded(order.id); }}>
                            <ArrowDropDownIcon sx={{ fontSize: 20, color: '#9ca3af', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                          </Box>

                          {/* Col 2: supplier name + rep tag / due amount on second line */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, py: 0.25 }}
                            onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                              <Typography
                                sx={{ fontSize: 12, fontWeight: 600, color: '#1a56db', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25, cursor: distPeer ? 'pointer' : 'default', '&:hover': distPeer ? { textDecoration: 'underline' } : {} }}
                                onClick={() => distPeer && setViewSupplierParty(distPeer)}
                              >
                                {distName}
                              </Typography>
                              {forRetailerName && (
                                <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', fontSize: 11, color: '#6d28d9', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  🏪 For: {forRetailerName}
                                </Box>
                              )}
                              {viaRepName && (
                                <Tooltip title={viaRepName}>
                                  <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', fontSize: 11, color: '#7c3aed', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'default' }}>
                                    👤 Rep
                                  </Box>
                                </Tooltip>
                              )}
                              {matchedItems.length > 0 && matchedItems.map(i => (
                                <Box key={i.id} sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: 0.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 10, color: '#1a56db', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {i.productName} × {i.qty}
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Col 3: phone — gap from supplier name pulled to 85% of the grid's normal 8px column
                              gap (6.8px), and wraps to a second line if it doesn't fit the column width */}
                          <Typography sx={{ fontSize: 11, color: '#000', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25, ml: '-1.2px' }}>
                            {distPeer?.phone ? `📞 ${distPeer.phone}` : ''}
                          </Typography>

                          {/* Col 4: location — area on its own line, city on the next, like the supplier name's wrap */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.25, minWidth: 0 }}>
                            {distPeer?.area && (
                              <Typography sx={{ fontSize: 11, color: '#000', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                                {distPeer.area}
                              </Typography>
                            )}
                            {distPeer?.city && (
                              <Typography sx={{ fontSize: 11, color: '#000', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                                {distPeer.city}
                              </Typography>
                            )}
                          </Box>

                          {/* Col 5: order # + value — both left-aligned, value on the line below the order id; shortage icon on a third line */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: '2px' }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#1a56db', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                              #{order.orderNumber || order.id}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                                ₹{formatTrackAmount(Number(order.totalAmount) - Number(order.totalTax))}
                              </Typography>
                              {hasShortage && (
                                <Tooltip arrow placement="right" title={
                                  <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>⚠ Shortages</Typography>
                                    {shortage!.shortages.map(s => (
                                      <Typography key={s.sku} sx={{ fontSize: 10 }}>{s.productName}: ordered {s.orderedQty}, received {s.receivedQty} (short <strong>{s.shortageQty}</strong>)</Typography>
                                    ))}
                                  </Box>
                                }>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'default' }}>
                                    <WarningIcon sx={{ fontSize: 13, color: '#f59e0b', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>Shortage</Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          {/* Col 6: status column and actions column are two fixed-width tracks so every row
                              lines up regardless of how many action buttons that row happens to show — a
                              variable-width actions box was letting 1-button rows shift left of 3-button rows.
                              Status is its own STATUS_COL_WIDTH track (right-aligned); actions is its own
                              ACTIONS_COL_WIDTH track (left-aligned, so the unused space lands on the right,
                              which is also where the trailing pr gives the panel breathing room from the row
                              edge). Cancel and Check Items never apply at the same time (Cancel only for
                              PLACED/VIEWED, Check Items only for DELIVERED) so they share one slot. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px', ml: '2px', pr: 1.5 }}
                            onClick={(e) => e.stopPropagation()}>
                          {/* status + cancel reason — left-aligned within its column */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.4, width: STATUS_COL_WIDTH, flexShrink: 0 }}>
                            <Box sx={{ px: 1, py: 0.3, borderRadius: 0.5, bgcolor: statusDef.bg, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', textAlign: 'center', width: (isRejected && order.cancelReason) ? 61 : 80 }}>
                              {statusDef.label.toUpperCase()}
                            </Box>
                            {isRejected && order.cancelReason && (
                              <Tooltip title={order.cancelReason} arrow placement="left" componentsProps={{ tooltip: { sx: { bgcolor: '#ef4444', color: '#fff' } }, arrow: { sx: { color: '#ef4444' } } }}>
                                <InfoIcon sx={{ fontSize: 15, color: '#ef4444', cursor: 'default', flexShrink: 0 }} />
                              </Tooltip>
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.25, width: ACTIONS_COL_WIDTH, flexShrink: 0 }}>
                            <Tooltip title="Repeat">
                              <IconButton size="small"
                                sx={{ p: 0, width: 18, height: 18, minHeight: 0, bgcolor: '#097969', color: '#fff', border: 'none', borderRadius: '4px', '&:hover': { bgcolor: '#075c50' } }}
                                onClick={() => setRepeatConfirmOrder(order)}>
                                <ReplayIcon sx={{ fontSize: 14, fontWeight: 700, stroke: 'currentColor', strokeWidth: 0.6 }} />
                              </IconButton>
                            </Tooltip>
                            {(order.status === 'PLACED' || order.status === 'VIEWED') ? (
                              <Tooltip title="Cancel">
                                <IconButton size="small"
                                  sx={{ p: 0, width: 18, height: 18, minHeight: 0, bgcolor: '#FA5F55', color: '#fff', border: 'none', borderRadius: '4px', '&:hover': { bgcolor: '#e14a40' } }}
                                  onClick={() => { setCancelTargetOrder(order); setCancelReason(''); }}>
                                  <CloseIcon sx={{ fontSize: 14, fontWeight: 700, stroke: 'currentColor', strokeWidth: 0.6 }} />
                                </IconButton>
                              </Tooltip>
                            ) : canCheckItems ? (
                              <Tooltip title="Check Items">
                                <IconButton size="small"
                                  sx={{ p: 0, width: 17, height: 17, minHeight: 0, bgcolor: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: '4px', '&:hover': { bgcolor: '#ddd6fe', borderColor: '#a78bfa' } }}
                                  onClick={() => { setCsvTargetOrder(order); setCheckItemsDlg(true); }}>
                                  <FactCheckIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {order.invoiceFileName && (
                              <Tooltip title="Download Invoice">
                                <IconButton size="small"
                                  sx={{ p: 0, width: 18, height: 18, minHeight: 0, bgcolor: '#1a56db', color: '#fff', border: 'none', borderRadius: '4px', '&:hover': { bgcolor: '#1543ad' } }}
                                  onClick={() => handleDownloadInvoice(order)}>
                                  <DownloadIcon sx={{ fontSize: 14, fontWeight: 700, stroke: 'currentColor', strokeWidth: 0.6 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          </Box>
                        </Box>

                        {/* Expanded detail — supplier details first, then order id, items, and value */}
                        {expanded && (
                          <Box sx={{ px: 2, py: 1, bgcolor: '#fafbfc', borderBottom: '1px solid #e5e7eb' }}
                            onClick={(e) => e.stopPropagation()}>
                            <Typography sx={{ fontSize: 11, color: '#374151' }}>
                              <Box component="span" sx={{ fontWeight: 700, color: '#152346' }}>{distName}</Box>
                              {distPeer?.phone && <> · {distPeer.phone}</>}
                              {distLocation && <> · {distLocation}</>}
                              {distPeer?.gstin && <> · GST: {distPeer.gstin}</>}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                              <Typography sx={{ fontSize: 11, color: '#000' }}>
                                Order #{order.orderNumber || order.id} · {itemCount} item{itemCount !== 1 ? 's' : ''} · ₹{formatTrackAmount(Number(order.totalAmount) - Number(order.totalTax))} · placed {timeStr}
                              </Typography>
                              {viaRepName && (
                                <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', color: '#7c3aed', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  👤 Via Rep: {viaRepName}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })
            ) : viewMode === 'supplier' ? (
              /* ── BY SUPPLIER view ── */
              pagedBySupplier.map(({ dateKey, allOrders: dayOrders, dayTotal, dayItems, distributors }) => {
                const d = new Date(dateKey);
                const now = new Date();
                const isToday = d.toDateString() === now.toDateString();
                const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
                const isYesterday = d.toDateString() === yesterday.toDateString();
                const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                const dayPart = isToday ? 'Today' : isYesterday ? 'Yesterday' : d.toLocaleDateString('en-GB', { weekday: 'long' });

                return (
                  <Box key={dateKey}>
                    {/* Date group header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 0.6, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346', minWidth: 160 }}>
                        {datePart} <Box component="span" sx={{ color: '#008080' }}>[{dayPart}]</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                        Orders: <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayOrders.length}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                        Items: <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayItems}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                        Total: <Box component="span" sx={{ fontWeight: 700, color: '#152346' }}>₹{formatTrackAmount(dayTotal)}</Box>
                      </Typography>
                    </Box>

                    {/* Distributor summary rows */}
                    {distributors.map(({ distId, distOrders }) => {
                      const expandKey = `${dateKey}|${distId}`;
                      const isExpanded = expandedSuppliers.has(expandKey);
                      const distName = distributorNames.get(distId) ?? `Supplier #${distId}`;
                      const distInfo = distributorInfo.get(distId);
                      const distLocation = [distInfo?.area, distInfo?.city].filter(Boolean).join(', ');
                      const distTotal = distOrders.reduce((s, o) => s + (Number(o.totalAmount) - Number(o.totalTax)), 0);
                      const distItems = distOrders.reduce((s, o) => s + (o.items?.length ?? 0), 0);
                      const hasAnyShortage = distOrders.some(o => (shortageStore.get(o.id)?.shortages.length ?? 0) > 0);

                      return (
                        <Box key={distId}>
                          {/* Distributor row */}
                          <Box
                            onClick={() => setExpandedSuppliers(prev => {
                              const next = new Set(prev);
                              if (next.has(expandKey)) next.delete(expandKey); else next.add(expandKey);
                              return next;
                            })}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' } }}
                          >
                            {isExpanded
                              ? <ArrowDownIcon sx={{ fontSize: 16, color: '#6b7280', flexShrink: 0 }} />
                              : <ArrowRightIcon sx={{ fontSize: 16, color: '#6b7280', flexShrink: 0 }} />
                            }
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a56db', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {distName}
                                </Typography>
                                {hasAnyShortage && (
                                  <Tooltip title="Has orders with shortages" placement="top">
                                    <WarningIcon sx={{ fontSize: 13, color: '#f59e0b', flexShrink: 0 }} />
                                  </Tooltip>
                                )}
                              </Box>
                              {(distLocation || distInfo?.phone) && (
                                <Typography sx={{ fontSize: 10, color: '#6b7280' }}>
                                  {distLocation}{distLocation && distInfo?.phone ? ' · ' : ''}{distInfo?.phone ? `📞 ${distInfo.phone}` : ''}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
                              {distOrders.length} order{distOrders.length !== 1 ? 's' : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
                              {distItems} item{distItems !== 1 ? 's' : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', flexShrink: 0, minWidth: 90, textAlign: 'right' }}>
                              ₹{formatTrackAmount(distTotal)}
                            </Typography>
                          </Box>

                          {/* Expanded: individual order rows */}
                          {isExpanded && distOrders.map(order => {
                            const shortage = shortageStore.get(order.id);
                            const hasShortage = (shortage?.shortages.length ?? 0) > 0;
                            const canCheckItems = order.status === 'DELIVERED';
                            const statusDef = ORDER_STATUS_MAP[order.status] ?? { label: order.status, bg: '#9ca3af' };
                            const itemCount = order.items?.length ?? 0;
                            const isRejected = order.status === 'REJECTED' || order.status === 'CANCELLED';
                            const forRetailerName = isRep
                              ? (retailerNames.get(order.retailerTenantId) ?? `Retailer #${order.retailerTenantId}`)
                              : null;
                            const viaRepName = !isRep ? (order.salesRepName ?? null) : null;

                            return (
                              <Box key={order.id} onClick={() => navigate(`/orders/my/${order.id}`)}
                                sx={{ display: 'flex', alignItems: 'center', pl: 5, pr: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', bgcolor: viaRepName ? '#faf5ff' : '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, minHeight: 44 }}>
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a56db', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                                    #{order.orderNumber || order.id}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontSize: 11, color: '#374151', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                                    {itemCount} item{itemCount !== 1 ? 's' : ''} · ₹{formatTrackAmount(Number(order.totalAmount))}
                                  </Typography>
                                  {hasShortage && (
                                    <Box component="span" sx={{ flexShrink: 0, color: '#f59e0b', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap' }}>
                                      ⚠ {shortage!.shortages.length} shortage{shortage!.shortages.length !== 1 ? 's' : ''}
                                    </Box>
                                  )}
                                  {forRetailerName && (
                                    <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: '5px', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', fontSize: 9, color: '#6d28d9', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      🏪 For: {forRetailerName}
                                    </Box>
                                  )}
                                  {viaRepName && (
                                    <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', fontSize: 9, color: '#7c3aed', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      👤 Via Rep: {viaRepName}
                                    </Box>
                                  )}
                                  </Box>
                                </Box>
                                <Box sx={{ pl: 1, mr: 2, width: 120, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, flexShrink: 0 }}>
                                  {hasShortage && (
                                    <Tooltip arrow placement="left" title={
                                      <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>⚠ Shortages</Typography>
                                        {shortage!.shortages.map(s => (
                                          <Typography key={s.sku} sx={{ fontSize: 10 }}>{s.productName}: ordered {s.orderedQty}, received {s.receivedQty} (short <strong>{s.shortageQty}</strong>)</Typography>
                                        ))}
                                      </Box>
                                    }>
                                      <WarningIcon sx={{ fontSize: 15, color: '#f59e0b', cursor: 'default' }} />
                                    </Tooltip>
                                  )}
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 0, height: 18, borderRadius: 0.5, bgcolor: statusDef.bg, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', textAlign: 'center', width: 80 }}>
                                    {statusDef.label.toUpperCase()}
                                  </Box>
                                  {isRejected && order.cancelReason && (
                                    <Tooltip title={order.cancelReason} arrow placement="left" componentsProps={{ tooltip: { sx: { bgcolor: '#ef4444', color: '#fff' } }, arrow: { sx: { color: '#ef4444' } } }}>
                                      <InfoIcon sx={{ fontSize: 15, color: '#ef4444', cursor: 'default' }} />
                                    </Tooltip>
                                  )}
                                </Box>
                                {/* Repeat first, then Cancel or Check Items (never both) in the freed-up slot, then Download
                                    Invoice if uploaded. Fixed width (not flexShrink: 0 alone) keeps this column's left
                                    edge — and therefore the status column right before it — aligned across rows that
                                    render a different number of buttons. */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, width: 230, flexShrink: 0, ml: '20px' }} onClick={e => e.stopPropagation()}>
                                  <Button size="small" variant="contained" disableElevation color="inherit"
                                    startIcon={<ReplayIcon sx={{ fontSize: '11px !important' }} />}
                                    sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#097969', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#075c50' } }}
                                    onClick={() => setRepeatConfirmOrder(order)}>
                                    Repeat
                                  </Button>
                                  {(order.status === 'PLACED' || order.status === 'VIEWED') ? (
                                    <Button size="small" variant="contained" disableElevation color="inherit"
                                      sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#FA5F55', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#e14a40' } }}
                                      onClick={() => { setCancelTargetOrder(order); setCancelReason(''); }}>
                                      Cancel
                                    </Button>
                                  ) : canCheckItems ? (
                                    <Button size="small" variant="outlined" color="inherit"
                                      startIcon={<FactCheckIcon sx={{ fontSize: order.invoiceFileName ? '9px !important' : '11px !important' }} />}
                                      sx={{ fontSize: order.invoiceFileName ? 9 : 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#ede9fe', color: '#7c3aed', borderColor: '#c4b5fd', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#ddd6fe', borderColor: '#a78bfa' } }}
                                      onClick={() => { setCsvTargetOrder(order); setCheckItemsDlg(true); }}>
                                      Check Items
                                    </Button>
                                  ) : null}
                                  {order.invoiceFileName && (
                                    <Button size="small" variant="contained" disableElevation color="inherit"
                                      startIcon={<DownloadIcon sx={{ fontSize: '11px !important' }} />}
                                      sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#1a56db', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#1543ad' } }}
                                      onClick={() => handleDownloadInvoice(order)}>
                                      Invoice
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })
            ) : viewMode === 'product' ? (
              /* ── BY PRODUCT view ── */
              (() => {
                // Re-group paged product rows by date
                const dateMap = new Map<string, typeof pagedProductRows>();
                for (const row of pagedProductRows) {
                  if (!dateMap.has(row.dateKey)) dateMap.set(row.dateKey, []);
                  dateMap.get(row.dateKey)!.push(row);
                }
                const pagedByProd = Array.from(dateMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));

                return allProductRows.length === 0 ? null : (
                  <>
                    {/* Column header — sticky */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(100px,3fr) minmax(150px,2fr) minmax(100px,52px) minmax(100px,56px) minmax(100px,72px) minmax(100px,72px) minmax(100px,56px) minmax(100px,88px)', px: 1.5, py: 0.6, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 2 }}>
                      {(['PRODUCT', 'SUPPLIER', 'QTY', 'FREE', 'MRP', 'PRICE', 'DISC', 'TOTAL'] as const).map((h, i) => (
                        <Typography key={h} sx={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textAlign: i >= 2 ? 'right' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>

                    {pagedByProd.map(([dateKey, rows]) => {
                      const d = new Date(dateKey + 'T00:00:00');
                      const now = new Date();
                      const isToday = d.toDateString() === now.toDateString();
                      const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
                      const isYesterday = d.toDateString() === yesterday.toDateString();
                      const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                      const dayPart = isToday ? 'Today' : isYesterday ? 'Yesterday' : d.toLocaleDateString('en-GB', { weekday: 'long' });
                      const dateTotal = rows.reduce((s, r) => s + r.prod.totalAmount, 0);
                      const dateTotalItems = rows.reduce((s, r) => s + r.prod.totalQty, 0);

                      return (
                        <Box key={dateKey}>
                          {/* Date group header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 0.75, bgcolor: '#e8edf5', borderBottom: '1px solid #d1d5db', borderTop: '1px solid #d1d5db', position: 'sticky', top: 35, zIndex: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', minWidth: 200 }}>
                              {datePart} <Box component="span" sx={{ color: '#008080' }}>[{dayPart}]</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              Products: <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{rows.length}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                              Items: <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{Math.round(dateTotalItems)}</Box>
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#152346' }}>₹{formatTrackAmount(dateTotal)}</Typography>
                          </Box>

                          {rows.map(({ prod }) => {
                            const expandKey = `product|${dateKey}|${prod.name}`;
                            const isExpanded = expandedSuppliers.has(expandKey);
                            const hasAnyShortage = prod.orderList.some((o: Order) => (shortageStore.get(o.id)?.shortages.length ?? 0) > 0);
                            const distLabel = prod.distIds.size === 1
                              ? (distributorNames.get([...prod.distIds][0]) ?? `Supplier #${[...prod.distIds][0]}`)
                              : `${prod.distIds.size} suppliers`;

                            return (
                              <Box key={prod.name}>
                                <Box
                                  onClick={() => setExpandedSuppliers(prev => {
                                    const next = new Set(prev);
                                    if (next.has(expandKey)) next.delete(expandKey); else next.add(expandKey);
                                    return next;
                                  })}
                                  sx={{ display: 'grid', gridTemplateColumns: 'minmax(100px,3fr) minmax(150px,2fr) minmax(100px,52px) minmax(100px,56px) minmax(100px,72px) minmax(100px,72px) minmax(100px,56px) minmax(100px,88px)', px: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, alignItems: 'center' }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                    {isExpanded
                                      ? <ArrowDownIcon sx={{ fontSize: 14, color: '#6b7280', flexShrink: 0 }} />
                                      : <ArrowRightIcon sx={{ fontSize: 14, color: '#6b7280', flexShrink: 0 }} />
                                    }
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a56db', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prod.name}</Typography>
                                      {prod.sku && <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>{prod.sku}</Typography>}
                                    </Box>
                                    {hasAnyShortage && (
                                      <Tooltip title="Some orders have shortages" placement="top">
                                        <WarningIcon sx={{ fontSize: 12, color: '#f59e0b', flexShrink: 0, ml: 0.5 }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Typography sx={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{distLabel}</Typography>
                                  <Typography sx={{ fontSize: 12, fontWeight: 700, textAlign: 'right', color: '#111827' }}>{prod.totalQty}</Typography>
                                  <Typography sx={{ fontSize: 12, textAlign: 'right', color: prod.totalFree > 0 ? '#16a34a' : '#9ca3af', fontWeight: prod.totalFree > 0 ? 700 : 400 }}>
                                    {prod.totalFree > 0 ? prod.totalFree : '—'}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, textAlign: 'right', color: '#6b7280' }}>{prod.mrp > 0 ? `₹${prod.mrp.toFixed(2)}` : '—'}</Typography>
                                  <Typography sx={{ fontSize: 11, textAlign: 'right', color: '#374151', fontWeight: 600 }}>{prod.unitPrice > 0 ? `₹${prod.unitPrice.toFixed(2)}` : '—'}</Typography>
                                  <Typography sx={{ fontSize: 11, textAlign: 'right', color: '#6b7280' }}>{prod.discountPct > 0 ? `${prod.discountPct}%` : '—'}</Typography>
                                  <Typography sx={{ fontSize: 12, fontWeight: 800, textAlign: 'right', color: '#152346' }}>₹{formatTrackAmount(prod.totalAmount)}</Typography>
                                </Box>

                                {isExpanded && prod.orderList.map((order: Order) => {
                                  const matchedItem = (order.items ?? []).find((i: import('@/types/order').OrderItem) => i.productName === prod.name);
                                  const distName = distributorNames.get(order.distributorTenantId) ?? `Supplier #${order.distributorTenantId}`;
                                  const distInfo = distributorInfo.get(order.distributorTenantId);
                                  const distLocation = [distInfo?.area, distInfo?.city].filter(Boolean).join(', ');
                                  const shortage = shortageStore.get(order.id);
                                  const hasShortage = (shortage?.shortages.length ?? 0) > 0;
                                  const canCheckItems = order.status === 'DELIVERED';
                                  const statusDef = ORDER_STATUS_MAP[order.status] ?? { label: order.status, bg: '#9ca3af' };
                                  const isRejected = order.status === 'REJECTED' || order.status === 'CANCELLED';
                                  const forRetailerName = isRep
                                    ? (retailerNames.get(order.retailerTenantId) ?? `Retailer #${order.retailerTenantId}`)
                                    : null;
                                  const viaRepName = !isRep ? (order.salesRepName ?? null) : null;

                                  return (
                                    <Box key={order.id} onClick={() => navigate(`/orders/my/${order.id}`)}
                                      sx={{ display: 'flex', alignItems: 'center', pl: 4, pr: 1.5, py: 0.75, borderBottom: '1px solid #f3f4f6', bgcolor: viaRepName ? '#faf5ff' : '#fafafe', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, minHeight: 48 }}>
                                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a56db', whiteSpace: 'nowrap' }}>#{order.orderNumber || order.id}</Typography>
                                        <Box sx={{ minWidth: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#1a56db', whiteSpace: 'normal', wordBreak: 'break-word' }}>{distName}</Typography>
                                          {distLocation && <Typography sx={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>{distLocation}</Typography>}
                                          {distInfo?.phone && <Typography sx={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>📞 {distInfo.phone}</Typography>}
                                        </Box>
                                        {matchedItem && (
                                          <Box sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: 0.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 10, color: '#1a56db', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            {matchedItem.qty}{Number(matchedItem.freeQty) > 0 ? ` + ${matchedItem.freeQty} free` : ''} pcs
                                          </Box>
                                        )}
                                        {forRetailerName && (
                                          <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: '5px', bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', fontSize: 9, color: '#6d28d9', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            🏪 For: {forRetailerName}
                                          </Box>
                                        )}
                                        {viaRepName && (
                                          <Box component="span" sx={{ flexShrink: 0, px: 0.75, py: 0.1, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', fontSize: 9, color: '#7c3aed', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            👤 Via Rep: {viaRepName}
                                          </Box>
                                        )}
                                      </Box>
                                      <Box sx={{ pl: 1, mr: 2, width: 120, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, flexShrink: 0 }}>
                                        {hasShortage && (
                                          <Tooltip arrow placement="left" title={
                                            <Box>
                                              <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>⚠ Shortages</Typography>
                                              {shortage!.shortages.map(s => (
                                                <Typography key={s.sku} sx={{ fontSize: 10 }}>{s.productName}: ordered {s.orderedQty}, received {s.receivedQty} (short <strong>{s.shortageQty}</strong>)</Typography>
                                              ))}
                                            </Box>
                                          }>
                                            <WarningIcon sx={{ fontSize: 15, color: '#f59e0b', cursor: 'default' }} />
                                          </Tooltip>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 0, height: 18, borderRadius: 0.5, bgcolor: statusDef.bg, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', textAlign: 'center', width: 80 }}>
                                          {statusDef.label.toUpperCase()}
                                        </Box>
                                        {isRejected && order.cancelReason && (
                                          <Tooltip title={order.cancelReason} arrow placement="left" componentsProps={{ tooltip: { sx: { bgcolor: '#ef4444', color: '#fff' } }, arrow: { sx: { color: '#ef4444' } } }}>
                                            <InfoIcon sx={{ fontSize: 15, color: '#ef4444', cursor: 'default' }} />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      {/* Repeat first, then Cancel or Check Items (never both) in the freed-up slot, then Download
                                          Invoice if uploaded. Fixed width keeps this column's left edge — and therefore the
                                          status column right before it — aligned across rows with a different button count. */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, width: 230, flexShrink: 0, ml: '20px' }} onClick={e => e.stopPropagation()}>
                                        <Button size="small" variant="contained" disableElevation color="inherit"
                                          startIcon={<ReplayIcon sx={{ fontSize: '11px !important' }} />}
                                          sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#097969', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#075c50' } }}
                                          onClick={() => setRepeatConfirmOrder(order)}>
                                          Repeat
                                        </Button>
                                        {(order.status === 'PLACED' || order.status === 'VIEWED') ? (
                                          <Button size="small" variant="contained" disableElevation color="inherit"
                                            sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#FA5F55', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#e14a40' } }}
                                            onClick={() => { setCancelTargetOrder(order); setCancelReason(''); }}>
                                            Cancel
                                          </Button>
                                        ) : canCheckItems ? (
                                          <Button size="small" variant="outlined" color="inherit"
                                            startIcon={<FactCheckIcon sx={{ fontSize: order.invoiceFileName ? '9px !important' : '11px !important' }} />}
                                            sx={{ fontSize: order.invoiceFileName ? 9 : 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#ede9fe', color: '#7c3aed', borderColor: '#c4b5fd', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#ddd6fe', borderColor: '#a78bfa' } }}
                                            onClick={() => { setCsvTargetOrder(order); setCheckItemsDlg(true); }}>
                                            Check Items
                                          </Button>
                                        ) : null}
                                        {order.invoiceFileName && (
                                          <Button size="small" variant="contained" disableElevation color="inherit"
                                            startIcon={<DownloadIcon sx={{ fontSize: '11px !important' }} />}
                                            sx={{ fontSize: 11, px: 1, py: 0, minWidth: 0, minHeight: 0, height: 18, lineHeight: 1, fontWeight: 700, bgcolor: '#1a56db', color: '#fff', borderRadius: '4px', textTransform: 'none', '&:hover': { bgcolor: '#1543ad' } }}
                                            onClick={() => handleDownloadInvoice(order)}>
                                            Invoice
                                          </Button>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </>
                );
              })()
            ) : null
            }
          </Box>

            {/* ── Pagination footer — part of the scrollable content, not pinned to the viewport ── */}
            {(() => {
              const total = viewMode === 'product' ? allProductRows.length
                : filteredOrders.length;
              const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
              const label = viewMode === 'product' ? 'products' : 'orders';
              return (
                <PaginationFooter
                  page={pageNum}
                  totalPages={totalPages}
                  total={total}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setPageNum}
                  onRowsPerPageChange={v => setRowsPerPage(v as 50 | 100)}
                  label={label}
                />
              );
            })()}
          </Box>
        )}
      </Box>

      {/* Mobile sidebar Drawer */}
      <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { width: 230, fontSize: 13 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.5 }}>Filter By</Typography>
            <Tooltip title="Refresh orders">
              <span>
                <IconButton size="small" onClick={() => { fetchOrders(); setSidebarOpen(false); }} disabled={loading} sx={{ p: 0.3 }}>
                  {loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {/* Search */}
          <Box sx={{ px: 1.25, py: 0.75, borderBottom: '1px solid #e5e7eb' }}>
            <TextField size="small" fullWidth placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: '#9ca3af' }} /></InputAdornment>, sx: { fontSize: 12, '& input': { py: '5px' } } }} />
          </Box>
          {/* Mode tabs */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
            {([
              { id: 'distributor', label: 'Supplier', Icon: StoreIcon },
              { id: 'product',     label: 'Product',  Icon: CategoryIcon },
              { id: 'reports',     label: 'Reports',  Icon: ReportsIcon },
            ] as { id: TrackFilterMode; label: string; Icon: React.ElementType }[]).map(({ id, label, Icon }) => (
              <Box key={id} onClick={() => { setFilterMode(id); setSelectedDistributorId(null); setSelectedProduct(null); setSidebarOpen(false); }}
                sx={{ flex: 1, py: 0.75, cursor: 'pointer', fontSize: 10, fontWeight: filterMode === id ? 700 : 500,
                  color: filterMode === id ? '#1a56db' : '#6b7280',
                  borderBottom: filterMode === id ? '2px solid #1a56db' : '2px solid transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                <Icon sx={{ fontSize: 12 }} />{label}
              </Box>
            ))}
          </Box>
          {/* Sidebar list */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filterMode === 'distributor' && (
              <>
                <Box sx={sidebarItemSx(selectedDistributorId === null)} onClick={() => { setSelectedDistributorId(null); setSidebarOpen(false); }}>
                  <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#374151' }}>All Suppliers</Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{orders.length}</Typography>
                </Box>
                {filteredDistributorEntries.map(([id, count]) => (
                  <Box key={id} sx={sidebarItemSx(selectedDistributorId === id)} onClick={() => { selectDistributor(id); setSidebarOpen(false); }}>
                    <Typography sx={{ flex: 1, fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {distributorNames.get(id) ?? `Supplier #${id}`}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{count}</Typography>
                  </Box>
                ))}
              </>
            )}
            {filterMode === 'product' && (
              <>
                <Box sx={sidebarItemSx(selectedProduct === null)} onClick={() => { setSelectedProduct(null); setSidebarOpen(false); }}>
                  <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#374151' }}>All Products</Typography>
                  <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>{productEntries.reduce((s, [, c]) => s + c, 0)}</Typography>
                </Box>
                {filteredProductEntries.map(([name, count]) => (
                  <Box key={name} sx={sidebarItemSx(selectedProduct === name)} onClick={() => { selectProduct(name); setSidebarOpen(false); }}>
                    <Typography sx={{ flex: 1, fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{count}</Typography>
                  </Box>
                ))}
              </>
            )}
            {filterMode === 'reports' && (
              <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Today by Supplier</Typography>
                  {todayBySupplier.length === 0
                    ? <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>No orders today.</Typography>
                    : todayBySupplier.map(([id, cnt]) => (
                        <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, cursor: 'pointer', borderRadius: 0.5, px: 0.5, '&:hover': { bgcolor: '#f0f7ff' } }}
                          onClick={() => { setFilterMode('distributor'); setSelectedDistributorId(id); setDateFilter('TODAY'); setSidebarOpen(false); }}>
                          <Typography sx={{ fontSize: 11, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{distributorNames.get(id) ?? `#${id}`}</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a56db', flexShrink: 0, ml: 0.5 }}>{cnt}</Typography>
                        </Box>
                      ))}
                </Box>
                <Box sx={{ borderTop: '1px solid #f3f4f6', pt: 1 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Quick Views</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 0.5, bgcolor: dateFilter === 'TODAY' ? '#eff6ff' : 'transparent', '&:hover': { bgcolor: '#f0f7ff' } }}
                    onClick={() => { setDateFilter(d => d === 'TODAY' ? 'ALL' : 'TODAY'); setFilterMode('distributor'); setSelectedDistributorId(null); setSidebarOpen(false); }}>
                    <Typography sx={{ fontSize: 11, color: '#374151' }}>Today&apos;s Orders</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a56db' }}>{todayOrders.length}</Typography>
                  </Box>
                  {([
                    { label: 'Scheme Items',    count: schemeOrderCount,   filter: 'schemes' as QuickFilter, color: '#7c3aed' },
                    { label: 'Shortage Orders', count: shortageOrderCount, filter: 'shortages' as QuickFilter, color: '#dc2626' },
                  ]).map(r => (
                    <Box key={r.filter} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 0.5, cursor: 'pointer', borderRadius: 0.5, bgcolor: quickFilter === r.filter ? '#eff6ff' : 'transparent', '&:hover': { bgcolor: '#f0f7ff' } }}
                      onClick={() => { setQuickFilter(q => q === r.filter ? 'all' : r.filter); setFilterMode('distributor'); setSelectedDistributorId(null); setSidebarOpen(false); }}>
                      <Typography sx={{ fontSize: 11, color: '#374151' }}>{r.label}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: r.color }}>{r.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <PartyDetailDialog
        party={viewSupplierParty}
        onClose={() => setViewSupplierParty(null)}
        onViewOutstanding={p => { setViewSupplierParty(null); setViewOutstandingParty(p); }}
      />
      <OutstandingInvoicesDialog
        tenantPartyId={viewOutstandingParty?.linkedTenantParty?.id ?? null}
        partyName={viewOutstandingParty?.name}
        onClose={() => setViewOutstandingParty(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MyOrdersPage
// ---------------------------------------------------------------------------

export function MyOrdersPage() {
  const user = useAuthStore(s => s.user);
  const location = useLocation();
  //const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem('myOrdersTab');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [myOrdersView, setMyOrdersView] = useState<'date' | 'supplier' | 'product'>(() => {
    return (sessionStorage.getItem('myOrdersView') as 'date' | 'supplier' | 'product' | null) ?? 'date';
  });

  const isRep = user?.tenantType === 'REPRESENTATIVE';

  // Rep: initial retailer from navigation state (e.g. "Book Order" button on My Retailers table)
  const navState = location.state as {
    bookOrderTab?: number;
    ts?: number;
    myOrdersView?: 'date' | 'supplier';
    selectedRetailerId?: number;
    selectedRetailerName?: string;
    repeatOrder?: {
      orderNumber: string;
      distributorTenantId: number;
      distributorName: string;
      items: Array<{
        productId: number;
        productName: string;
        sku: string;
        uom?: string;
        qty: number;
        freeQty?: number;
        ptr?: number;
        unitPrice?: number;
        mrp?: number;
      }>;
    };
  } | null;
  const [initialRetailerId] = useState<number | undefined>(navState?.selectedRetailerId);
  const [initialRetailerName] = useState<string | undefined>(navState?.selectedRetailerName);

  // Search + manual date range for the My Orders (Date/Supplier/Product Wise) tabs — rendered
  // here, beside those tabs, instead of inside OrderStatusTab's own header, to leave more
  // vertical room for the order list. Mirrors Order Inbox: explicit trigger, never live.
  const savedTrackSearch = loadSessionFilters<{
    trackSearchMode: 'supplier' | 'orderId' | 'product' | 'phone'; trackSearchInput: string;
    appliedTrackSearch: { mode: 'supplier' | 'orderId' | 'product' | 'phone'; query: string } | null;
    manualStartDate: string; manualEndDate: string;
    appliedDateRange: { startDate?: string; endDate?: string } | null;
  }>('myOrdersTrackSearch');
  const [trackSearchMode, setTrackSearchMode] = useState<'supplier' | 'orderId' | 'product' | 'phone'>(() => savedTrackSearch.trackSearchMode ?? 'supplier');
  const [trackSearchInput, setTrackSearchInput] = useState(() => savedTrackSearch.trackSearchInput ?? '');
  const [appliedTrackSearch, setAppliedTrackSearch] = useState<{ mode: 'supplier' | 'orderId' | 'product' | 'phone'; query: string } | null>(() => savedTrackSearch.appliedTrackSearch ?? null);
  const [manualStartDate, setManualStartDate] = useState(() => savedTrackSearch.manualStartDate ?? '');
  const [manualEndDate, setManualEndDate] = useState(() => savedTrackSearch.manualEndDate ?? '');
  const [appliedDateRange, setAppliedDateRange] = useState<{ startDate?: string; endDate?: string } | null>(() => savedTrackSearch.appliedDateRange ?? null);
  const manualDateRangeError = manualStartDate && manualEndDate && manualStartDate > manualEndDate ? 'Start date must be on or before end date' : '';

  useEffect(() => {
    saveSessionFilters('myOrdersTrackSearch', { trackSearchMode, trackSearchInput, appliedTrackSearch, manualStartDate, manualEndDate, appliedDateRange });
  }, [trackSearchMode, trackSearchInput, appliedTrackSearch, manualStartDate, manualEndDate, appliedDateRange]);

  function runTrackSearch() {
    if (manualDateRangeError) return;
    const trimmed = trackSearchInput.trim();
    setAppliedTrackSearch(trimmed ? { mode: trackSearchMode, query: trimmed } : null);
    setAppliedDateRange(manualStartDate || manualEndDate ? { startDate: manualStartDate || undefined, endDate: manualEndDate || undefined } : null);
  }

  function clearTrackSearch() {
    setTrackSearchInput('');
    setAppliedTrackSearch(null);
  }

  function clearManualDateRange() {
    setManualStartDate('');
    setManualEndDate('');
    setAppliedDateRange(null);
  }

  // React when navigated here with a specific tab (e.g. from Book Order / My Orders menu).
  // Depend on navState.ts so this re-fires every time handleBookOrder navigates here,
  // even when the component is already mounted on /orders.
  useEffect(() => {
    if (navState?.bookOrderTab != null) {
      setTab(navState.bookOrderTab);
      sessionStorage.setItem('myOrdersTab', String(navState.bookOrderTab));
    }
    if (navState?.myOrdersView) {
      setMyOrdersView(navState.myOrdersView as 'date' | 'supplier' | 'product');
      sessionStorage.setItem('myOrdersView', navState.myOrdersView);
    }
    if (navState?.repeatOrder) {
      const rp = navState.repeatOrder;
      const repeatItems: OrderItem[] = rp.items.map(i => ({
        key: `repeat-${rp.distributorTenantId}-${i.productId}-${Date.now()}-${Math.random()}`,
        product: {
          productId: i.productId,
          sku: i.sku,
          name: i.productName,
          uom: i.uom ?? '',
          mrp: i.mrp,
          ptr: i.unitPrice ?? i.ptr,
          pts: i.unitPrice ?? i.ptr,
          approxRate: undefined,
          distributorTenantId: rp.distributorTenantId,
          distributorName: rp.distributorName,
          stockHidden: true,
          priceHidden: false,
          schemeHidden: true,
          availableQty: undefined,
          scheme: undefined,
          brand: undefined,
          packSize: undefined,
          distributorCity: undefined,
          minOrderQty: undefined,
          minOrderAmount: undefined,
        } as CrossTenantProduct,
        qty: i.qty,
        freeQty: i.freeQty ?? 0,
      }));
      setPlaceOrderItems(repeatItems);
      setTab(1);
      sessionStorage.setItem('myOrdersTab', '1');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navState?.ts]);

  function handleTabChange(_: React.SyntheticEvent, v: number) {
    setTab(v);
    sessionStorage.setItem('myOrdersTab', String(v));
    if (v === 2 && user) {
      const tid = user.tenantId;
      orderTemplatesApi.list().then(list => {
        const cached = loadTplCounts(tid);
        setDrafts(prev => {
          const prevMap = new Map(prev.map(d => [d.id, d]));
          return list.map(t => {
            const existing = prevMap.get(String(t.id));
            const itemCount = t.itemCount ?? existing?.itemCount ?? cached[String(t.id)];
            return { id: String(t.id), name: t.name, items: existing?.items ?? [], savedAt: t.savedAt, itemCount };
          });
        });
        list.forEach(t => { if (t.itemCount !== undefined) setTplCount(tid, String(t.id), t.itemCount); });
      }).catch(() => {});
    }
  }
  const [allDistributors, setAllDistributors] = useState<PeerTenant[]>([]);
  const [allPeerCities, setAllPeerCities] = useState<string[]>([]);
  const [priorityIds, setPriorityIds] = useState<number[]>([]);
  const [preferredCities, setPreferredCities] = useState<string[]>([]);
  const [defaultCity, setDefaultCity] = useState<string | undefined>(undefined);
  const [homeArea, setHomeArea] = useState<string>('');
  const [priorityLocations, setPriorityLocations] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [placeOrderItems, setPlaceOrderItems] = useState<OrderItem[]>(() =>
    user ? loadPlaceOrderItems(user.tenantId) : [],
  );
  const [drafts, setDrafts] = useState<DraftOrder[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editingDraftName, setEditingDraftName] = useState<string>('');
  const [templateMsg, setTemplateMsg] = useState('');
  /** Rep-only: retailers the rep can order on behalf of, loaded from localStorage + API. */
  const [myRetailers, setMyRetailers] = useState<{ id: number; name: string; area?: string; city?: string; gstin?: string; drugLicenseNumber?: string }[]>([]);

  const usePtr = user?.tenantType === 'RETAILER' || user?.tenantType === 'WHOLESALER' || isRep;

  // Persist place-order items whenever they change
  useEffect(() => {
    if (user) savePlaceOrderItems(user.tenantId, placeOrderItems);
  }, [user, placeOrderItems]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const savedPrefs = await orderPreferencesApi.get().catch(() => null);

        if (isRep) {
          // Representative: distributors are the approved supplier rep-requests.
          // Retailers are those the rep has toggled on (stored in localStorage).
          const [repRequestsResult, retailerPeersResult, me] = await Promise.all([
            getMyRepRequests().catch(() => [] as Awaited<ReturnType<typeof getMyRepRequests>>),
            tenantApi.listPeers(undefined, 0, 200, 'RETAILER').catch(() => ({ content: [] } as { content: PeerTenant[] })),
            tenantApi.getMe().catch(() => null),
          ]);
          if (cancelled) return;

          // Distributors = only APPROVED rep-requests
          const approvedSupplierIds = new Set(
            repRequestsResult
              .filter(r => r.status === 'APPROVED')
              .map(r => r.supplierTenantId),
          );
          const wholesalersResult = await tenantApi.listPeers(undefined, 0, 200, 'WHOLESALER').catch(() => ({ content: [] } as { content: PeerTenant[] }));
          if (cancelled) return;
          const wholesalers = wholesalersResult.content.filter(p => approvedSupplierIds.has(p.id));
          setAllDistributors(wholesalers);

          // Collect cities
          const citySet = new Set<string>();
          wholesalers.forEach(p => { if (p.city) citySet.add(p.city); });
          setAllPeerCities(Array.from(citySet).sort());

          const allIds = wholesalers.map(p => p.id);
          const savedSupplierIds = savedPrefs?.supplierIds ?? [];
          setPriorityIds(savedSupplierIds.filter(id => allIds.includes(id)));
          if (savedPrefs?.locations?.length) setPriorityLocations(savedPrefs.locations);

          // ── Manual retailers (localStorage) ────────────────────────────
          const repRetailerKey = 'rep_my_retailers';
          let manualRetailerIds: number[] = [];
          try {
            const raw = localStorage.getItem(repRetailerKey);
            if (raw) {
              const all = JSON.parse(raw) as { repTenantId: number; retailerIds: number[] }[];
              const entry = all.find(e => e.repTenantId === user.tenantId);
              if (entry) manualRetailerIds = entry.retailerIds;
            }
          } catch { /* ignore */ }

          // ── Auto retailers from APPROVED supplier assignments ────────────
          const autoRetailerIdSet = new Set<number>();
          repRequestsResult
            .filter(r => r.status === 'APPROVED')
            .forEach(r => {
              if (!r.retailerMode || r.retailerMode === 'ALL') {
                // ALL scope — include every retailer peer we loaded
                retailerPeersResult.content.forEach(p => autoRetailerIdSet.add(p.id));
              } else {
                // LIMITED scope — include only the specific IDs the supplier picked
                (r.retailerIds ?? []).forEach(id => autoRetailerIdSet.add(id));
              }
            });

          // ── Merge (deduplicate) and resolve to names ─────────────────────
          const effectiveIds = [...new Set([...manualRetailerIds, ...autoRetailerIdSet])];
          const retailerMap = new Map(retailerPeersResult.content.map(p => [p.id, p]));
          const retailers = effectiveIds
            .filter(id => retailerMap.has(id))
            .map(id => {
              const p = retailerMap.get(id)!;
              return { id, name: p.name, area: p.area, city: p.city, gstin: p.gstin, drugLicenseNumber: p.drugLicenseNumber };
            });
          setMyRetailers(retailers);

          if (me?.city) setDefaultCity(me.city);
          if (me?.area) setHomeArea(me.area);
          const savedCitiesRep = savedPrefs?.cities ?? [];
          if (savedCitiesRep.length > 0) {
            setPreferredCities(savedCitiesRep);
          } else if (me?.city) {
            const initial = [me.city];
            setPreferredCities(initial);
            orderPreferencesApi.update({ supplierIds: savedSupplierIds, cities: initial, locations: savedPrefs?.locations ?? [] }).catch(() => {});
          }
        } else {
          // RETAILER / WHOLESALER: original flow
          const [peersResult, me] = await Promise.all([
            tenantApi.listPeers(undefined, 0, 1000),
            tenantApi.getMe().catch(() => null),
          ]);
          if (cancelled) return;

          const wholesalers = peersResult.content.filter(p => p.tenantType === 'WHOLESALER');
          setAllDistributors(wholesalers);

          const citySet = new Set<string>();
          peersResult.content.forEach(p => { if (p.city) citySet.add(p.city); });
          setAllPeerCities(Array.from(citySet).sort());

          const allIds = wholesalers.map(p => p.id);
          const savedSupplierIds = savedPrefs?.supplierIds ?? [];
          setPriorityIds(savedSupplierIds.filter(id => allIds.includes(id)));
          if (savedPrefs?.locations?.length) setPriorityLocations(savedPrefs.locations);

          if (me?.city) setDefaultCity(me.city);
          if (me?.area) setHomeArea(me.area);
          const savedCities = savedPrefs?.cities ?? [];
          if (savedCities.length > 0) {
            if (me?.city && !savedCities.includes(me.city)) {
              const updated = [me.city, ...savedCities];
              setPreferredCities(updated);
              orderPreferencesApi.update({ supplierIds: savedSupplierIds, cities: updated, locations: savedPrefs?.locations ?? [] }).catch(() => {});
            } else {
              setPreferredCities(savedCities);
            }
          } else if (me?.city) {
            const initial = [me.city];
            setPreferredCities(initial);
            orderPreferencesApi.update({ supplierIds: savedSupplierIds, cities: initial, locations: [] }).catch(() => {});
          }
        }
      } catch {
        // silently ignore — user will see empty sidebar
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isRep]);

  // Load templates from backend; fall back to migrating legacy localStorage on first load
  useEffect(() => {
    if (!user) return;
    const tid = user.tenantId;
    const toDraft = (t: { id: number; name: string; savedAt: string; itemCount?: number }) => ({
      id: String(t.id), name: t.name, items: [] as OrderItem[], savedAt: t.savedAt,
      itemCount: t.itemCount ?? loadTplCounts(tid)[String(t.id)],
    });
    orderTemplatesApi.list().then(async (list) => {
      if (list.length > 0) {
        setDrafts(list.map(toDraft));
        list.forEach(t => { if (t.itemCount !== undefined) setTplCount(tid, String(t.id), t.itemCount); });
      } else {
        // One-time migration: push any localStorage templates to backend
        const legacy = loadLegacyDrafts(tid);
        for (const d of legacy) {
          try { await orderTemplatesApi.create(d.name, d.items as unknown[]); } catch { /* dup name – skip */ }
        }
        if (legacy.length > 0) {
          localStorage.removeItem(`${DRAFTS_KEY}_${tid}`);
          const fresh = await orderTemplatesApi.list();
          setDrafts(fresh.map(toDraft));
          fresh.forEach(t => { if (t.itemCount !== undefined) setTplCount(tid, String(t.id), t.itemCount); });
        }
      }
    }).catch(() => {
      // Backend unavailable — fall back to localStorage
      if (user) setDrafts(loadLegacyDrafts(user.tenantId));
    });
  }, [user]);

  function savePriority(ids: number[]) {
    setPriorityIds(ids);
    orderPreferencesApi.update({ supplierIds: ids, cities: preferredCities, locations: priorityLocations }).catch(() => {});
  }

  function handleCityReorder(cities: string[]) {
    setPreferredCities(cities);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities, locations: priorityLocations }).catch(() => {});
  }

  function handleCityRemove(city: string) {
    const updated = preferredCities.filter(c => c !== city);
    setPreferredCities(updated);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities: updated, locations: priorityLocations }).catch(() => {});
  }

  function handleCityAdd(city: string) {
    if (preferredCities.includes(city)) return;
    const updated = [...preferredCities, city];
    setPreferredCities(updated);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities: updated, locations: priorityLocations }).catch(() => {});
  }

  function handleLocationReorder(locs: string[]) {
    setPriorityLocations(locs);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities: preferredCities, locations: locs }).catch(() => {});
  }

  function handleLocationRemove(loc: string) {
    const updated = priorityLocations.filter(l => l !== loc);
    setPriorityLocations(updated);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities: preferredCities, locations: updated }).catch(() => {});
  }

  function handleLocationAdd(loc: string) {
    if (priorityLocations.includes(loc)) return;
    const updated = [...priorityLocations, loc];
    setPriorityLocations(updated);
    orderPreferencesApi.update({ supplierIds: priorityIds, cities: preferredCities, locations: updated }).catch(() => {});
  }

  async function handleSaveTemplate(name: string, items: OrderItem[]): Promise<void> {
    // Client-side duplicate check for instant feedback
    const duplicate = drafts.some(d => d.name.trim().toLowerCase() === name.trim().toLowerCase());
    if (duplicate) {
      const conflictErr = { status: 409, message: `A template named "${name}" already exists.` };
      throw conflictErr;
    }
    try {
      const saved = await orderTemplatesApi.create(name, items as unknown[]);
      const sid = String(saved.id);
      if (user) setTplCount(user.tenantId, sid, items.length);
      setDrafts(prev => [...prev, { id: sid, name: saved.name, items, savedAt: saved.savedAt, itemCount: items.length }]);
      setTemplateMsg(`Template "${saved.name}" saved successfully.`);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      if (status === 409) throw err; // bubble up so dialog shows inline error
      // Offline fallback for other errors
      const d: DraftOrder = { id: `local-${Date.now()}`, name, items, savedAt: new Date().toISOString(), itemCount: items.length };
      setDrafts(prev => [...prev, d]);
    }
  }

  async function handleRenameTemplate(id: string, newName: string): Promise<void> {
    const numId = Number(id);
    if (isNaN(numId)) return;
    const saved = await orderTemplatesApi.update(numId, newName, null);
    setDrafts(prev => prev.map(d => d.id === id ? { ...d, name: saved.name } : d));
    if (editingDraftId === id) setEditingDraftName(saved.name);
    setTemplateMsg(`Template renamed to "${saved.name}".`);
  }

  async function handleEditTemplate(draft: DraftOrder): Promise<void> {
    // Load items then switch to Place Order in editing mode
    let items = draft.items;
    if (items.length === 0) {
      const numId = Number(draft.id);
      if (!isNaN(numId)) {
        try { items = (await orderTemplatesApi.get(numId)).items as OrderItem[]; } catch { /* ignore */ }
      }
    }
    setPlaceOrderItems(items as OrderItem[]);
    setEditingDraftId(draft.id);
    setEditingDraftName(draft.name);
    setTab(1);
  }

  async function handleUpdateTemplate(items: OrderItem[]): Promise<void> {
    const numId = Number(editingDraftId);
    if (isNaN(numId) || !editingDraftId) return;
    const saved = await orderTemplatesApi.update(numId, null, items as unknown[]);
    if (user) setTplCount(user.tenantId, editingDraftId, items.length);
    setDrafts(prev => prev.map(d => d.id === editingDraftId ? { ...d, items, savedAt: saved.savedAt, itemCount: items.length } : d));
    setEditingDraftId(null);
    setEditingDraftName('');
    setTemplateMsg(`Template "${saved.name}" updated successfully.`);
  }

  function handleDeleteTemplate(id: string) {
    setDrafts(prev => prev.filter(d => d.id !== id));
    if (user) delTplCount(user.tenantId, id);
    const numId = Number(id);
    if (!isNaN(numId)) {
      orderTemplatesApi.delete(numId).catch(() => {});
    }
  }

  async function handleLoadTemplate(draft: DraftOrder) {
    // If items are already populated (local fallback), use them directly
    if (draft.items.length > 0) {
      setPlaceOrderItems(draft.items);
      setTab(1);
      return;
    }
    // Fetch full items from backend
    const numId = Number(draft.id);
    if (!isNaN(numId)) {
      try {
        const detail = await orderTemplatesApi.get(numId);
        setPlaceOrderItems(detail.items as OrderItem[]);
      } catch { /* ignore — empty items */ }
    }
    setTab(1);
    sessionStorage.setItem('myOrdersTab', '1');
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 76px)', overflow: 'hidden' }}>
      {/* Title + Tabs */}
      <Box sx={{ px: 3, flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: tab === 0 ? 0.5 : 0 }}>
        {tab === 0 ? (
          <Tabs
            value={myOrdersView}
            onChange={(_, v: 'date' | 'supplier' | 'product') => {
              setMyOrdersView(v);
              sessionStorage.setItem('myOrdersView', v);
            }}
            sx={{ minHeight: 40 }}
          >
            <Tab value="date"     label="Date Wise"     sx={{ minHeight: 40, fontSize: 12, textTransform: 'none', fontWeight: 700 }} />
            <Tab value="supplier" label="Supplier Wise" sx={{ minHeight: 40, fontSize: 12, textTransform: 'none', fontWeight: 700 }} />
            <Tab value="product"  label="Product Wise"  sx={{ minHeight: 40, fontSize: 12, textTransform: 'none', fontWeight: 700 }} />
          </Tabs>
        ) : (
          <Tabs value={tab} onChange={handleTabChange} sx={{ minHeight: 40 }}>
            <Tab value={1} label="Place Order"      sx={{ minHeight: 40, fontSize: 12, textTransform: 'none', fontWeight: 700 }} />
            <Tab value={2} label="Order Templates"  sx={{ minHeight: 40, fontSize: 12, textTransform: 'none', fontWeight: 700 }} />
          </Tabs>
        )}

        {tab === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Search by select + input + icon button — mirrors Order Inbox */}
            <Select
              size="small"
              value={trackSearchMode}
              onChange={e => setTrackSearchMode(e.target.value as 'supplier' | 'orderId' | 'product' | 'phone')}
              sx={{ fontSize: 12, minWidth: 120, flexShrink: 0, '& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px 0 0 4px' } }}
            >
              <MenuItem value="supplier" sx={{ fontSize: 12 }}>Supplier Name</MenuItem>
              <MenuItem value="phone"    sx={{ fontSize: 12 }}>Phone Number</MenuItem>
              <MenuItem value="orderId"  sx={{ fontSize: 12 }}>Order ID</MenuItem>
              <MenuItem value="product"  sx={{ fontSize: 12 }}>Product Name</MenuItem>
            </Select>
            <TextField
              size="small"
              placeholder={trackSearchMode === 'product' ? 'Search product…' : trackSearchMode === 'orderId' ? 'Search order ID…' : trackSearchMode === 'phone' ? 'Search phone number…' : 'Search supplier name…'}
              value={trackSearchInput}
              onChange={e => setTrackSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runTrackSearch(); }}
              sx={{ flex: '1 1 140px', maxWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '0 4px 4px 0', fontSize: 13 } }}
            />
            <Tooltip title="Search">
              <IconButton
                size="small"
                onClick={runTrackSearch}
                disabled={Boolean(manualDateRangeError)}
                sx={{ color: appliedTrackSearch?.query.trim() ? '#7c3aed' : undefined, bgcolor: appliedTrackSearch?.query.trim() ? '#ede9fe' : undefined, borderRadius: 1 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {appliedTrackSearch?.query.trim() && (
              <Tooltip title="Clear search">
                <IconButton size="small" onClick={clearTrackSearch} sx={{ borderRadius: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Date range — manual, alongside the Time Period quick-filter, same as Order Inbox */}
            <CalIcon sx={{ color: '#9ca3af', fontSize: 18, ml: 0.5 }} />
            <TextField type="date" size="small" label="Start Date" value={manualStartDate}
              error={Boolean(manualDateRangeError)}
              onChange={e => setManualStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ max: manualEndDate || undefined }}
              sx={{ width: 140, '& .MuiInputBase-input': { fontSize: 12 } }} />
            <TextField type="date" size="small" label="End Date" value={manualEndDate}
              error={Boolean(manualDateRangeError)}
              onChange={e => setManualEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ min: manualStartDate || undefined }}
              sx={{ width: 140, '& .MuiInputBase-input': { fontSize: 12 } }} />
            {(manualStartDate || manualEndDate) && !manualDateRangeError && (
              <Tooltip title="Clear date range">
                <IconButton size="small" onClick={clearManualDateRange} sx={{ borderRadius: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
      {tab === 0 && manualDateRangeError && (
        <Alert severity="error" sx={{ mx: 3, mt: 1, fontSize: 12, flexShrink: 0 }}>{manualDateRangeError}</Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {tab === 0 && (
              <OrderStatusTab
                initialView={myOrdersView}
                appliedTrackSearch={appliedTrackSearch}
                appliedDateRange={appliedDateRange}
                clearTrackSearch={clearTrackSearch}
                clearManualDateRange={clearManualDateRange}
              />
            )}
            {tab === 1 && (
              <PlaceOrderTab
                allDistributors={allDistributors}
                allPeerCities={allPeerCities}
                priorityIds={priorityIds}
                onReorder={ids => savePriority(ids)}
                onRemove={id => savePriority(priorityIds.filter(x => x !== id))}
                onAdd={id => savePriority([...priorityIds, id])}
                usePtr={usePtr}
                orderItems={placeOrderItems}
                onOrderItemsChange={setPlaceOrderItems}
                onSaveTemplate={handleSaveTemplate}
                editingDraftId={editingDraftId}
                editingDraftName={editingDraftName}
                onUpdateTemplate={handleUpdateTemplate}
                onCancelEdit={() => { setEditingDraftId(null); setEditingDraftName(''); }}
                preferredCities={preferredCities}
                onCityReorder={handleCityReorder}
                onCityRemove={handleCityRemove}
                onCityAdd={handleCityAdd}
                defaultCity={defaultCity}
                homeArea={homeArea}
                priorityLocations={priorityLocations}
                onLocationReorder={handleLocationReorder}
                onLocationRemove={handleLocationRemove}
                onLocationAdd={handleLocationAdd}
                isRep={isRep}
                myRetailers={myRetailers}
                initialRetailerId={initialRetailerId}
                initialRetailerName={initialRetailerName}
              />
            )}
            {tab === 2 && (
              <OrderTemplatesTab
                usePtr={usePtr}
                drafts={drafts}
                onLoadTemplate={handleLoadTemplate}
                onEditTemplate={handleEditTemplate}
                onRenameTemplate={handleRenameTemplate}
                onDeleteTemplate={handleDeleteTemplate}
                onFetchItems={id => orderTemplatesApi.get(Number(id)).then(t => t.items as OrderItem[])}
              />
            )}
          </Box>
        </Box>
      )}

      <Snackbar
        open={Boolean(templateMsg)}
        autoHideDuration={3000}
        onClose={() => setTemplateMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setTemplateMsg('')} severity="success" sx={{ width: '100%' }}>
          {templateMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
